import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { Ticket } from '../../types';
import FileUpload from '../common/FileUpload';
import PartsAutocomplete from '../common/PartsAutocomplete';
import DigitalSignature from '../common/DigitalSignature';

interface ServiceWorkflowProps {
  ticketId: string;
  deviceSerialNumber?: string;
  ticket?: Ticket; // Optional ticket prop to avoid re-loading
  onClose: () => void;
}

interface WorkflowStep {
  stepId?: string;
  step_id?: string;
  stepNumber?: number;
  step_number?: number;
  stepName?: string;
  step_name?: string;
  stepDescription?: string;
  step_description?: string;
  isOptional?: boolean;
  is_optional?: boolean;
  status: 'not_started' | 'in_progress' | 'completed' | 'skipped';
  agentId?: string;
  agent_id?: string;
  agentName?: string;
  agent_name?: string;
  startedAt?: string;
  started_at?: string;
  completedAt?: string;
  completed_at?: string;
  definition: any;
  [key: string]: any; // For dynamic step-specific fields
}

// interface Workflow {
//   workflowId: string;
//   ticketId: string;
//   deviceSerialNumber: string;
//   workflowNumber: string;
//   currentStep: number;
//   status: string;
//   steps: WorkflowStep[];
// }

const ServiceWorkflow: React.FC<ServiceWorkflowProps> = ({ ticketId, deviceSerialNumber: propDeviceSerial, ticket: propTicket, onClose }) => {
  const { user, isAuthenticated, token } = useAuth();
  const { isDarkMode } = useTheme();
  const [workflow, setWorkflow] = useState<any>(null);
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState<any>({});
  const [isCreating, setIsCreating] = useState(false);
  const [activeStep, setActiveStep] = useState(1);
  const [formData, setFormData] = useState<any>({});
  const [agents, setAgents] = useState<any[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [pdfReports, setPdfReports] = useState<any[]>([]);
  const [attachments, setAttachments] = useState<any>({});  // Store attachments per step
  const [uploadingFiles, setUploadingFiles] = useState<any>({});  // Track upload progress
  const [loadingWorkflow, setLoadingWorkflow] = useState(false);
  const [workflowLoadAttempted, setWorkflowLoadAttempted] = useState<string | null>(null);
  const [availableParts, setAvailableParts] = useState<any[]>([]);
  const [loadingParts, setLoadingParts] = useState(false);
  
  // Signature-related state
  const [signatures, setSignatures] = useState<any[]>([]);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [availableApprovers, setAvailableApprovers] = useState<any[]>([]);
  const [selectedApprover, setSelectedApprover] = useState<string>('');
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [signatureType, setSignatureType] = useState<'repair' | 'approval'>('repair');
  
  // Request Approval modal state
  const [showRequestApprovalModal, setShowRequestApprovalModal] = useState(false);
  const [selectedNotificationAgent, setSelectedNotificationAgent] = useState<string>('');
  const [sendingNotification, setSendingNotification] = useState(false);

  // Quote Notification modal state
  const [showQuoteNotificationModal, setShowQuoteNotificationModal] = useState(false);
  const [selectedQuoteAgent, setSelectedQuoteAgent] = useState<string>('');
  const [sendingQuoteNotification, setSendingQuoteNotification] = useState(false);

  // Report approval state
  const [reportApprovals, setReportApprovals] = useState<any[]>([]);


  // Reset workflow load attempt when ticket ID changes
  useEffect(() => {
    if (workflowLoadAttempted && workflowLoadAttempted !== ticketId) {
      console.log('🔄 Ticket ID changed, resetting workflow load attempt');
      setWorkflowLoadAttempted(null);
      setWorkflow(null);
      setCreateError(null);
      setLoading(true); // Reset loading state for new ticket
    }
  }, [ticketId, workflowLoadAttempted]);

  // Load available parts when component mounts
  useEffect(() => {
    const loadAvailableParts = async () => {
      try {
        setLoadingParts(true);
        const response = await apiService.get('/parts/dropdown');
        if (response.success) {
          setAvailableParts(response.parts);
          console.log('✅ Parts loaded for autocomplete:', response.parts.length);
        } else {
          console.error('❌ Parts API response indicates failure:', response);
          setAvailableParts([]);
        }
      } catch (error) {
        console.error('❌ Error loading parts:', error);
        setAvailableParts([]);
      } finally {
        setLoadingParts(false);
      }
    };

    loadAvailableParts();
  }, []);

  useEffect(() => {
    console.log('🔄 ServiceWorkflow useEffect triggered:', {
      ticketId,
      isAuthenticated,
      hasWorkflow: !!workflow,
      workflowTicketId: workflow?.ticketId,
      agentsCount: agents.length,
      workflowLoadAttempted
    });
    
    if (!isAuthenticated) {
      console.log('❌ Not authenticated, setting error');
      setCreateError('You must be logged in to access service workflows.');
      setLoading(false);
      return;
    }
    
    // Only load if we haven't attempted to load this ticket's workflow yet or if ticketId has changed
    if (workflowLoadAttempted !== ticketId && !loadingWorkflow) {
      console.log('🔄 Loading workflow because:', {
        hasWorkflow: !!workflow,
        workflowTicketId: workflow?.ticketId,
        requestedTicketId: ticketId,
        workflowLoadAttempted,
        loading,
        loadingWorkflow,
        needsReload: workflowLoadAttempted !== ticketId
      });
      // Use createWorkflow instead of loadTicketAndWorkflow for better error handling
      // The createWorkflow function handles both creating new workflows and loading existing ones
      loadTicketDataAndCreateOrLoadWorkflow();
    } else {
      console.log('✅ Workflow load skipped because:', {
        alreadyAttempted: workflowLoadAttempted === ticketId,
        loading,
        loadingWorkflow,
        reason: workflowLoadAttempted === ticketId ? 'already attempted' : 'workflow loading in progress'
      });
    }
    
    // Only load agents if we don't have them yet
    if (agents.length === 0) {
      console.log('🔄 Loading agents because we have none');
      loadAgents();
    } else {
      console.log('✅ Agents already loaded, skipping');
    }
  }, [ticketId, isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load PDF reports when workflow is available
  useEffect(() => {
    if (workflow?.workflowId) {
      loadPdfReports();
      
      // Manual button state check for debugging
      setTimeout(() => {
        console.log('🔍 Manual button state check after PDF reports load:');
        console.log('  - Current signatures array:', signatures);
        console.log('  - Number of signatures:', signatures.length);
        console.log('  - hasRepairSignature():', hasRepairSignature());
        console.log('  - hasApprovalRequest():', hasApprovalRequest());
        console.log('  - canRequestApproval():', canRequestApproval());
        
        // Check individual signatures
        signatures.forEach((sig, index) => {
          console.log(`  - Signature ${index}:`, {
            signature_id: sig.signature_id,
            step_number: sig.step_number,
            signature_type: sig.signature_type,
            status: sig.status
          });
        });
      }, 1000);
    }
  }, [workflow?.workflowId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load attachments when active step changes
  useEffect(() => {
    if (workflow?.steps && workflow.steps.length > 0) {
      const currentStepObj = workflow.steps.find((s: WorkflowStep) => 
        s.stepNumber === activeStep || s.step_number === activeStep
      );
      const stepId = currentStepObj?.stepId || currentStepObj?.step_id;
      if (stepId && !attachments[stepId]) {
        loadStepAttachments(stepId);
      }
    }
  }, [activeStep, workflow?.steps, attachments]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update locked fields when ticket changes (step 1 only)
  useEffect(() => {
    if (activeStep === 1 && (propTicket || ticket)) {
      const ticketData = propTicket || ticket;
      const currentStepObj = workflow?.steps?.find((s: WorkflowStep) => 
        s.stepNumber === 1 || s.step_number === 1
      );
      
      if (currentStepObj && ticketData) {
        console.log('🔄 Updating locked device fields from ticket data');
        setFormData((prev: any) => ({
          ...prev,
          deviceSerialNumber: ticketData.deviceSerialNumber || prev.deviceSerialNumber,
          deviceModel: ticketData.deviceModel || prev.deviceModel
        }));
      }
    }
  }, [activeStep, propTicket?.deviceSerialNumber, propTicket?.deviceModel, ticket?.deviceSerialNumber, ticket?.deviceModel, workflow?.steps]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch device information for warranty expiration (step 1 only)
  useEffect(() => {
    const fetchDeviceInfo = async () => {
      if (activeStep === 1 && workflow?.deviceSerialNumber && !formData.warrantyExpiration) {
        try {
          console.log('🔍 Fetching device info for warranty expiration:', workflow.deviceSerialNumber);
          
          // Get device by serial number
          const devices = await apiService.getDevices({ search: workflow.deviceSerialNumber });
          
          if (devices?.devices?.length > 0) {
            const device = devices.devices.find((d: any) => 
              d.serialNumber === workflow.deviceSerialNumber
            );
            
            if (device && device.warrantyExpires) {
              console.log('✅ Found device warranty expiration:', device.warrantyExpires);
              
              // Format date for HTML date input (YYYY-MM-DD)
              const warrantyDate = new Date(device.warrantyExpires);
              const formattedDate = warrantyDate.toISOString().split('T')[0];
              
              console.log('📅 Formatted warranty date for form:', formattedDate);
              
              setFormData((prev: any) => ({
                ...prev,
                warrantyExpiration: formattedDate
              }));
            } else {
              console.log('ℹ️ No warranty expiration found for device:', workflow.deviceSerialNumber);
            }
          }
        } catch (error) {
          console.error('❌ Error fetching device info:', error);
        }
      }
    };

    fetchDeviceInfo();
  }, [activeStep, workflow?.deviceSerialNumber, formData.warrantyExpiration]);

  // Load signatures when workflow changes
  useEffect(() => {
    console.log('🔴 SIGNATURES USEEFFECT TRIGGERED 🔴:', {
      hasWorkflow: !!workflow,
      workflowId: workflow?.workflowId,
      willCallLoadSignatures: !!workflow?.workflowId
    });
    
    if (workflow?.workflowId) {
      console.log('🔴 CALLING LOADSIGNATURES FOR WORKFLOW 🔴:', workflow.workflowId);
      loadSignatures();
      console.log('🔴 CALLING LOADAVAILABLEAPPROVERS 🔴');
      loadAvailableApprovers();
      console.log('🔴 CALLING LOADREPORTAPPROVALS 🔴');
      loadReportApprovals();
    } else {
      console.log('❌ Not calling loadSignatures because:', {
        noWorkflow: !workflow,
        noWorkflowId: !workflow?.workflowId
      });
    }
  }, [workflow?.workflowId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadPdfReports = async () => {
    if (!workflow?.workflowId) {
      console.log('⚠️ Cannot load PDF reports: no workflow ID');
      return;
    }
    
    try {
      console.log('🔍 Loading PDF reports for workflow:', workflow.workflowId);
      console.log('🔍 Workflow number:', workflow.workflowNumber);
      console.log('🔍 Full workflow object:', workflow);
      
      const response = await apiService.get(`/service-workflows/${workflow.workflowId}/pdf-reports`);
      console.log('📄 PDF reports response:', response);
      console.log('📄 Response type:', typeof response);
      console.log('📄 Response array length:', Array.isArray(response) ? response.length : 'Not an array');
      
      // DEBUG: Log detailed structure of each report
      if (Array.isArray(response) && response.length > 0) {
        console.log('📄 First report structure:', response[0]);
        console.log('📄 First report keys:', Object.keys(response[0]));
        console.log('📄 All reports signer data:', response.map(r => ({
          filename: r.filename,
          hasRepairSigner: !!r.repairSigner,
          hasApprovalSigner: !!r.approvalSigner,
          repairSignerData: r.repairSigner,
          approvalSignerData: r.approvalSigner
        })));
      }
      
      setPdfReports(response || []);
      console.log('✅ PDF reports loaded successfully, count:', (response || []).length);
    } catch (error: any) {
      console.error('❌ Error loading PDF reports:', error);
      console.error('❌ Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      // Set empty array on error to prevent UI issues
      setPdfReports([]);
    }
  };

  const generatePdfReport = async () => {
    if (!workflow?.workflowId || generatingPdf) return;

    try {
      setGeneratingPdf(true);
      console.log('🔄 Generating PDF report for workflow:', workflow.workflowId);
      console.log('🔍 API Base URL:', process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001');
      
      const response = await apiService.post(`/service-workflows/${workflow.workflowId}/generate-pdf-report`, {
        reportType: 'draft'
      });

      console.log('📄 PDF generation response:', response);

      if (response && response.success) {
        console.log('✅ PDF report generated:', response.filename);
        // Show success message to user (remove this alert as we have the table now)
        // alert(`✅ PDF report generated successfully: ${response.filename}`);
        // Reload PDF reports list
        await loadPdfReports();
      } else {
        console.warn('⚠️ PDF generation response indicates failure:', response);
        alert(`⚠️ PDF generation may have failed. Check console for details.`);
      }
    } catch (error: any) {
      console.error('❌ Error generating PDF report:', error);
      console.error('❌ Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        stack: error.stack
      });
      
      // Show error message to user
      const errorMessage = error.response?.data?.error || error.message || 'Unknown error occurred';
      alert(`❌ Error generating PDF report: ${errorMessage}`);
    } finally {
      setGeneratingPdf(false);
    }
  };

  const downloadPdfReport = async (filename: string) => {
    try {
      console.log('📄 Downloading PDF report:', filename);
      
      // Debug authentication state
      const localStorageToken = localStorage.getItem('auth_token');
      const userData = localStorage.getItem('user_data');
      console.log('🔐 Debug - LocalStorage token exists:', !!localStorageToken);
      console.log('🔐 Debug - Auth context token exists:', !!token);
      console.log('🔐 Debug - User data exists:', !!userData);
      console.log('🔐 Debug - Auth context authenticated:', isAuthenticated);
      console.log('🔐 Debug - Auth context user:', user);
      
      // Use localStorage token first, fallback to auth context token
      const authToken = localStorageToken || token;
      
      if (!authToken) {
        console.error('❌ No auth token available in localStorage or auth context');
        throw new Error('No authentication token found. Please log in again.');
      }
      
      console.log('✅ Using auth token from:', localStorageToken ? 'localStorage' : 'auth context');
      
      // Use consistent backend URL construction (same as PDF generation)
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
      const fullBackendUrl = backendUrl.startsWith('http') ? backendUrl : `https://${backendUrl}`;
      
      console.log('📄 PDF Download - Backend URL:', backendUrl);
      console.log('📄 PDF Download - Full Backend URL:', fullBackendUrl);
      console.log('📄 PDF Download - Filename:', filename);
      console.log('📄 PDF Download - Full URL:', `${fullBackendUrl}/api/service-workflows/reports/${filename}`);
      
      const response = await fetch(`${fullBackendUrl}/api/service-workflows/reports/${filename}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to download report: ${response.status} ${response.statusText}`);
      }

      // Create a blob from the response
      const blob = await response.blob();
      
      // Create a temporary URL for the blob
      const url = window.URL.createObjectURL(blob);
      
      // Create a temporary anchor element and trigger download
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = filename;
      a.target = '_blank'; // Open in new tab as fallback
      document.body.appendChild(a);
      a.click();
      
      // Clean up
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }, 100);

      console.log('✅ PDF report downloaded successfully');

    } catch (error: any) {
      console.error('❌ Error downloading PDF report:', error);
      const errorMessage = error.message || 'Unknown error occurred';
      alert(`❌ Error downloading PDF report: ${errorMessage}`);
    }
  };

  const deletePdfReport = async (filename: string) => {
    if (!window.confirm(`Are you sure you want to delete the report: ${filename}?`)) {
      return;
    }

    try {
      console.log('🗑️ Deleting PDF report:', filename);
      
      const response = await apiService.delete(`/service-workflows/reports/${filename}`);
      console.log('📄 Delete response:', response);

      if (response && response.success) {
        console.log('✅ PDF report deleted successfully');
        // Reload PDF reports list
        await loadPdfReports();
      } else {
        console.warn('⚠️ PDF deletion response indicates failure:', response);
        alert(`⚠️ Failed to delete report. Check console for details.`);
      }
    } catch (error: any) {
      console.error('❌ Error deleting PDF report:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Unknown error occurred';
      alert(`❌ Error deleting PDF report: ${errorMessage}`);
    }
  };

  // ==========================================
  // REPORT APPROVAL FUNCTIONS
  // ==========================================

  // Load report approvals for the current workflow
  const loadReportApprovals = async () => {
    if (!workflow?.workflowId) return;

    try {
      console.log('🔍 Loading report approvals for workflow:', workflow.workflowId);
      console.log('🔍 Report approvals API URL:', `/service-workflows/${workflow.workflowId}/reports/approvals`);
      const response = await apiService.get(`/service-workflows/${workflow.workflowId}/reports/approvals`);
      console.log('🔍 Report approvals API response:', response);
      if (response.success) {
        console.log('✅ Report approvals loaded:', response.approvals);
        setReportApprovals(response.approvals || []);
      }
    } catch (error) {
      console.error('🔴 ERROR LOADING REPORT APPROVALS (500 ERROR SOURCE?) 🔴:', error);
      setReportApprovals([]);
    }
  };



  // ==========================================
  // SIGNATURE FUNCTIONS
  // ==========================================

  // Load signatures for the current workflow
  const loadSignatures = async () => {
    if (!workflow?.workflowId) return;

    try {
      console.log('🔍 Loading signatures for workflow:', workflow.workflowId);
      console.log('🔍 Signatures API URL:', `/signatures/${workflow.workflowId}`);
      const response = await apiService.get(`/signatures/${workflow.workflowId}`);
      console.log('🔍 Signatures API response:', response);
      if (response.success) {
        console.log('✅ Signatures loaded successfully:', response.signatures);
        console.log('✅ Number of signatures:', response.signatures?.length || 0);
        setSignatures(response.signatures || []);
        
        // Force check approval button state after signatures load
        setTimeout(() => {
          console.log('🔍 Forcing approval button state check...');
          const hasRepair = hasRepairSignature();
          const canRequest = canRequestApproval();
          console.log('🔍 Post-load button state:', { hasRepair, canRequest });
        }, 100);
      } else {
        console.warn('⚠️ Signatures API response indicates failure:', response);
      }
    } catch (error) {
      console.error('🔴 ERROR LOADING SIGNATURES (500 ERROR SOURCE?) 🔴:', error);
      // For now, create mock signature data if repair was completed
      if (workflow.steps) {
        const step6 = workflow.steps.find((s: any) => s.stepNumber === 6);
        if (step6?.repair_signature_data) {
          console.log('🔄 Found repair signature in workflow data');
          setSignatures([{
            signature_id: 'mock-repair',
            workflow_id: workflow.workflowId,
            step_number: 6,
            signature_type: 'repair',
            signer_name: user?.firstName + ' ' + user?.lastName,
            signed_at: new Date().toISOString(),
            status: 'signed'
          }]);
        }
      }
    }
  };

  // Load all available agents for notifications
  const loadAvailableApprovers = async () => {
    if (!workflow?.workflowId) return;

    try {
      console.log('🔍 Loading all available agents for notifications:', workflow.workflowId);
      console.log('🔍 Agents API URL:', `/service-workflows/${workflow.workflowId}/available-approvers`);
      const response = await apiService.get(`/service-workflows/${workflow.workflowId}/available-approvers`);
      console.log('🔍 Agents API response:', response);
      if (response.success) {
        console.log('✅ Available agents loaded:', response.approvers);
        console.log('🔢 Number of agents:', response.approvers?.length || 0);
        setAvailableApprovers(response.approvers || []);
      } else {
        console.error('❌ API response indicates failure:', response);
        setAvailableApprovers([]);
      }
    } catch (error) {
      console.error('❌ ERROR LOADING AGENTS:', error);
      setAvailableApprovers([]);
    }
  };

  // Check if current user can sign repair report (is the repair technician)
  const canUserSignRepair = (): boolean => {
    if (!workflow || !user) return false;
    const step6 = workflow.steps?.find((s: any) => s.stepNumber === 6);
    return step6?.repairAgentId === user.id || 
           step6?.repair_agent_id === user.id || 
           step6?.agentId === user.id || 
           step6?.agent_id === user.id;
  };

  // Check if repair signature exists
  const hasRepairSignature = (): boolean => {
    console.log('🔍 Checking repair signature. All signatures:', signatures);
    console.log('🔍 Checking workflow steps for repair_signature_data:', workflow?.steps);
    
    // Check the new signature system - look for repair_signature_data in workflow steps
    const step6 = workflow?.steps?.find((s: any) => s.stepNumber === 6 || s.step_number === 6);
    const hasWorkflowSignature = !!(step6?.repair_signature_data);
    console.log('🔍 Step 6 found:', step6);
    console.log('🔍 Has repair_signature_data in workflow:', hasWorkflowSignature);
    
    // Check the old signature system - look in signatures array
    const repairSignature = signatures.find((s: any) => s.step_number === 6 && s.signature_type === 'repair_technician');
    console.log('🔍 Found repair signature (repair_technician):', repairSignature);
    
    const repairSignatureAlt = signatures.find((s: any) => s.step_number === 6 && s.signature_type === 'repair');
    console.log('🔍 Found repair signature (repair):', repairSignatureAlt);
    
    const hasOldSignature = repairSignature?.status === 'signed' || repairSignatureAlt?.status === 'signed';
    console.log('🔍 Has old signature system:', hasOldSignature);
    
    const hasSignature = hasWorkflowSignature || hasOldSignature;
    console.log('🔍 Final repair signature result (workflow OR old system):', hasSignature);
    
    return hasSignature;
  };

  // Check if approval request exists
  const hasApprovalRequest = (): boolean => {
    const approvalSignature = signatures.find((s: any) => s.step_number === 7 && s.signature_type === 'approver');
    return approvalSignature?.status === 'pending' || approvalSignature?.status === 'signed';
  };

  // Check if approval signature exists
  const hasApprovalSignature = (): boolean => {
    const approvalSignature = signatures.find((s: any) => s.step_number === 7 && s.signature_type === 'approver');
    return approvalSignature?.status === 'signed';
  };

  // Check if current user can request approval
  const canRequestApproval = (): boolean => {
    const hasRepair = hasRepairSignature();
    const hasApproval = hasApprovalRequest();
    const canRequest = hasRepair && !hasApproval;
    
    console.log('🔍 canRequestApproval() check:', {
      hasRepairSignature: hasRepair,
      hasApprovalRequest: hasApproval,
      canRequestApproval: canRequest
    });
    
    return canRequest;
  };

  // Check if current user can approve (is the selected approver)
  const canUserApprove = (): boolean => {
    if (!user) return false;
    const approvalSignature = signatures.find((s: any) => s.step_number === 7 && s.signature_type === 'approver');
    return approvalSignature?.signer_id === user.id;
  };

  // Handle repair signature
  const handleRepairSignature = () => {
    setSignatureType('repair');
    setShowSignatureModal(true);
  };

  // Handle approval signature
  const handleApprovalSignature = () => {
    // IMPROVED VALIDATION: Compare by user ID and email instead of names
    const currentUserId = user?.id;
    const currentUserEmail = user?.email;
    const currentUserName = user?.name || `${user?.firstName} ${user?.lastName}`.trim();
    
    console.log('🔍 APPROVAL DEBUG - Current user:', {
      id: currentUserId,
      email: currentUserEmail,
      name: currentUserName
    });
    
    // Get repair signer information from PDF reports
    const repairSigner = pdfReports.find((report: any) => 
      report.repairSigner && (report.repairSigner.name || report.repairSigner.email || report.repairSigner.id)
    )?.repairSigner;
    
    console.log('🔍 APPROVAL DEBUG - Repair signer:', repairSigner);
    
    if (repairSigner) {
      // Compare by ID first (most reliable), then email, then name as fallback
      const sameUser = (
        (currentUserId && repairSigner.id && currentUserId === repairSigner.id) ||
        (currentUserEmail && repairSigner.email && currentUserEmail.toLowerCase() === repairSigner.email.toLowerCase()) ||
        (currentUserName && repairSigner.name && currentUserName.toLowerCase() === repairSigner.name.toLowerCase())
      );
      
      console.log('🔍 APPROVAL DEBUG - Same user check:', {
        byId: currentUserId && repairSigner.id && currentUserId === repairSigner.id,
        byEmail: currentUserEmail && repairSigner.email && currentUserEmail.toLowerCase() === repairSigner.email.toLowerCase(),
        byName: currentUserName && repairSigner.name && currentUserName.toLowerCase() === repairSigner.name.toLowerCase(),
        finalResult: sameUser
      });
      
      if (sameUser) {
        alert('⚠️ Warning: You cannot approve your own repair work. Please request a different agent to approve this report using the "🔔 Request Approval" button.');
        return;
      }
    }
    
    setSignatureType('approval');
    setShowSignatureModal(true);
  };

  // Handle request approval - opens agent selector modal
  const handleRequestApproval = () => {
    console.log('🔔 Opening request approval modal');
    console.log('🔍 Current availableApprovers state:', availableApprovers);
    setShowRequestApprovalModal(true);
    // Load all available agents when modal opens
    loadAvailableApprovers();
  };

  // Send approval request notification to selected agent
  const sendApprovalNotification = async () => {
    if (!selectedNotificationAgent || !workflow?.workflowId) {
      console.error('❌ Missing agent selection or workflow ID');
      return;
    }

    try {
      setSendingNotification(true);
      console.log('📨 Sending approval notification to agent:', selectedNotificationAgent);
      
      const notificationData = {
        agentId: selectedNotificationAgent,
        workflowId: workflow.workflowId,
        workflowNumber: workflow.workflowNumber,
        message: `A new service report for workflow ${workflow.workflowNumber} requires your approval.`
      };

      const response = await apiService.post(`/service-workflows/${workflow.workflowId}/request-approval`, notificationData);
      
      if (response.success) {
        console.log('✅ Approval notification sent successfully');
        // Show success message (you can add a toast notification here)
        const selectedAgent = availableApprovers.find(agent => agent.id === selectedNotificationAgent);
        const agentName = selectedAgent ? `${selectedAgent.firstName} ${selectedAgent.lastName}` : 'the selected agent';
        alert(`Approval request sent successfully! ${agentName} will receive both a system notification and an email with a direct link to review the report.`);
        
        // Close modal and reset selection
        setShowRequestApprovalModal(false);
        setSelectedNotificationAgent('');
      } else {
        console.error('❌ Failed to send approval notification:', response);
        alert('Failed to send approval notification. Please try again.');
      }
    } catch (error) {
      console.error('❌ Error sending approval notification:', error);
      alert('Error sending approval notification. Please try again.');
    } finally {
      setSendingNotification(false);
    }
  };

  // Handle quote notification request - opens agent selector modal
  const handleQuoteNotification = () => {
    console.log('💰 Opening quote notification modal');
    console.log('🔍 Current availableApprovers state:', availableApprovers);
    setShowQuoteNotificationModal(true);
    // Load all available agents when modal opens
    loadAvailableApprovers();
  };

  // Send quote notification to selected agent
  const sendQuoteNotification = async () => {
    if (!selectedQuoteAgent || !workflow?.workflowId) {
      console.error('❌ Missing agent selection or workflow ID');
      return;
    }

    try {
      setSendingQuoteNotification(true);
      console.log('📨 Sending quote notification to agent:', selectedQuoteAgent);
      
      const notificationData = {
        agentId: selectedQuoteAgent,
        workflowId: workflow.workflowId,
        workflowNumber: workflow.workflowNumber,
        ticketId: workflow.ticketId || ticket?.id,
        message: `Quote required for service workflow ${workflow.workflowNumber}. Please create and/or approve the customer quote.`
      };

      const response = await apiService.post(`/service-workflows/${workflow.workflowId}/request-quote`, notificationData);
      
      if (response.success) {
        console.log('✅ Quote notification sent successfully');
        // Show success message (you can add a toast notification here)
        const selectedAgent = availableApprovers.find(agent => agent.id === selectedQuoteAgent);
        const agentName = selectedAgent ? `${selectedAgent.firstName} ${selectedAgent.lastName}` : 'the selected agent';
        alert(`Quote notification sent successfully! ${agentName} will receive both a system notification and an email with a direct link to create/approve the quote.`);
        
        // Close modal and reset selection
        setShowQuoteNotificationModal(false);
        setSelectedQuoteAgent('');
      } else {
        console.error('❌ Failed to send quote notification:', response);
        alert('Failed to send quote notification. Please try again.');
      }
    } catch (error) {
      console.error('❌ Error sending quote notification:', error);
      alert('Error sending quote notification. Please try again.');
    } finally {
      setSendingQuoteNotification(false);
    }
  };

  // Complete repair signature - Uses current logged-in agent automatically
  const completeRepairSignature = async (signatureData: string) => {
    if (!workflow?.workflowId) return;

    try {
      console.log('🖋️ Creating repair signature with current agent profile...');
      console.log('🔐 Current agent:', user?.name, user?.email);
      
      // Only send signature data - backend gets agent info from JWT token
      const response = await apiService.post(`/service-workflows/${workflow.workflowId}/signatures/repair`, {
        signatureData
      });

      if (response.success) {
        setShowSignatureModal(false);
        
        // Reload workflow data
        await loadWorkflow(true);
        await loadSignatures();
        await loadPdfReports();
        
        // SIMPLE FIX: Update step 6 form with current agent name after signing
        if (workflow?.steps) {
          const updatedSteps = workflow.steps.map((step: any) => {
            if (step.stepNumber === 6 || step.step_number === 6) {
              return {
                ...step,
                repairAgentId: user?.id,
                agent_name: user?.name || `${user?.firstName} ${user?.lastName}`.trim()
              };
            }
            return step;
          });
          setWorkflow({
            ...workflow,
            steps: updatedSteps
          });
        }
        
        // Show success message
        const pdfMessage = response.pdfRegenerated 
          ? `✅ Repair signature completed successfully using your agent profile! PDF automatically updated with your signature (${response.pdfFilename}).`
          : `✅ Repair signature completed successfully using your agent profile (${user?.name})!`;
        
        alert(pdfMessage);
      } else {
        console.error('Signature save failed:', response);
        alert('❌ Failed to save signature. Please try again.');
      }
    } catch (error: any) {
      console.error('Error saving signature:', error);
      alert(`❌ Error: ${error.response?.data?.error || error.message}`);
    }
  };

  // SIMPLIFIED: Send for approval - Just send a notification
  const requestApprovalSignature = async () => {
    if (!workflow?.workflowId || !selectedApprover) return;

    try {
      console.log('📨 Sending simple approval notification...');
      
      const response = await apiService.post(`/service-workflows/${workflow.workflowId}/notify-approval`, {
        approverId: selectedApprover,
        workflowNumber: workflow.workflowNumber
      });

      if (response.success) {
        setShowApprovalModal(false);
        setSelectedApprover('');
        alert(`✅ Approval notification sent to ${response.approverName}!`);
      }
    } catch (error: any) {
      console.error('Error sending approval notification:', error);
      alert(`❌ Error: ${error.response?.data?.error || error.message}`);
    }
  };

  // Complete approval signature - Uses current logged-in agent automatically
  const completeApprovalSignature = async (signatureData: string) => {
    if (!workflow?.workflowId) return;

    // DOUBLE VALIDATION: Simple name comparison
    const currentUserName = user?.name || `${user?.firstName} ${user?.lastName}`.trim();
    const repairSignerName = pdfReports.find((report: any) => 
      report.repairSigner && report.repairSigner.name
    )?.repairSigner?.name;
    
    if (repairSignerName && currentUserName === repairSignerName) {
      alert('⚠️ Warning: You cannot approve your own repair work. Please request a different agent to approve this report.');
      setShowSignatureModal(false);
      return;
    }

    try {
      console.log('🖋️ Creating approval signature with current agent profile...');
      console.log('🔐 Current agent:', user?.name, user?.email);

      // Only send signature data - backend gets agent info from JWT token
      const response = await apiService.post(`/service-workflows/${workflow.workflowId}/signatures/approval`, {
        signatureData
      });

      if (response.success) {
        setShowSignatureModal(false);
        
        // Reload workflow data
        await loadWorkflow(true);
        await loadSignatures();
        await loadPdfReports();
        
        // SIMPLE FIX: Update step 7 form with current agent name after signing
        if (workflow?.steps) {
          const updatedSteps = workflow.steps.map((step: any) => {
            if (step.stepNumber === 7 || step.step_number === 7) {
              return {
                ...step,
                approverAgentId: user?.id,
                agent_name: user?.name || `${user?.firstName} ${user?.lastName}`.trim()
              };
            }
            return step;
          });
          setWorkflow({
            ...workflow,
            steps: updatedSteps
          });
        }
        
        // Show success message
        const pdfMessage = response.pdfRegenerated 
          ? `✅ Approval signature saved successfully using your agent profile! PDF automatically updated with your signature (${response.pdfFilename}). Report is now fully approved!`
          : `✅ Approval signature saved successfully using your agent profile (${user?.name})! Report is now fully approved!`;
        
        alert(pdfMessage);
      } else {
        console.error('Approval signature save failed:', response);
        alert('❌ Failed to save approval signature. Please try again.');
      }
    } catch (error: any) {
      console.error('❌ Error saving approval signature:', error);
      alert(`❌ Error: ${error.response?.data?.error || error.message}`);
    }
  };

  // Get signature status for display
  const getSignatureStatus = (): string => {
    if (hasApprovalSignature()) return 'approved';
    if (hasApprovalRequest()) return 'signed, waiting approval';
    if (hasRepairSignature()) return 'repair signed, waiting for approval';
    return 'draft';
  };

  const checkApiHealth = async (): Promise<boolean> => {
    try {
      console.log('🩺 Checking API health...');
      console.log('🩺 Current auth token:', localStorage.getItem('auth_token') ? 'Present' : 'Missing');
      // Try a simple API call to verify connectivity
      const healthResult = await apiService.get('/tickets?limit=1');
      console.log('✅ API health check passed, result:', healthResult);
      return true;
    } catch (error: any) {
      console.error('❌ API health check failed:', error);
      console.error('❌ Health check error details:', {
        message: error.message,
        status: error.response?.status,
        responseData: error.response?.data
      });
      return false;
    }
  };

  const loadTicketDataAndCreateOrLoadWorkflow = async () => {
    try {
      setLoading(true);
      setCreateError(null);
      setWorkflowLoadAttempted(ticketId); // Mark that we've attempted to load for this ticket
      
      console.log('🔍 Starting loadTicketDataAndCreateOrLoadWorkflow for ticketId:', ticketId);
      
      // Use propTicket if available, otherwise load from API
      if (propTicket) {
        console.log('✅ Using ticket data from props:', propTicket);
        setTicket(propTicket);
        console.log('🔍 Ticket data from props check:', {
          hasId: !!propTicket.id,
          hasServiceWorkflow: !!propTicket.hasServiceWorkflow,
          ticketKeys: Object.keys(propTicket)
        });
      } else {
        // First check if the API is responsive
        const isApiHealthy = await checkApiHealth();
        if (!isApiHealthy) {
          throw new Error('Backend server is not responding. Please ensure the server is running and try again.');
        }
        
        // Load ticket information from API
        console.log('🔍 Loading ticket information from API for:', ticketId);
        const ticketResponse = await apiService.getTicketById(ticketId);
        console.log('📥 Ticket response:', ticketResponse);
        
        if (!ticketResponse || !ticketResponse.id) {
          console.error('❌ Invalid ticket response:', ticketResponse);
          throw new Error('Invalid ticket data received from server');
        }
        
        setTicket(ticketResponse);
        console.log('✅ Ticket loaded successfully from API');
        console.log('🔍 Ticket data structure check:', {
          hasId: !!ticketResponse.id,
          hasServiceWorkflow: !!ticketResponse.hasServiceWorkflow,
          ticketKeys: Object.keys(ticketResponse)
        });
      }
      
      // Then try to create/load workflow using the more robust createWorkflow logic
      console.log('🔍 Starting workflow creation/loading...');
      try {
        await createWorkflowInternal();
        console.log('✅ Workflow creation/loading completed successfully');
      } catch (workflowError) {
        console.error('❌ Workflow creation/loading failed:', workflowError);
        console.error('❌ Workflow error details:', {
          message: workflowError instanceof Error ? workflowError.message : String(workflowError),
          stack: workflowError instanceof Error ? workflowError.stack : undefined
        });
        // Don't re-throw workflow errors here - let them be handled by createWorkflowInternal
        // which has better error handling for workflow-specific issues
      }
      
    } catch (error: any) {
      console.error('❌ Error in loadTicketDataAndCreateOrLoadWorkflow:', error);
      console.error('❌ Error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        responseData: error.response?.data
      });
      
      let errorMessage = 'Failed to load ticket data.';
      
      // Provide more specific error messages based on the error type
      if (error.response?.status === 401) {
        errorMessage = 'Authentication failed. Please log in again.';
      } else if (error.response?.status === 403) {
        errorMessage = 'You do not have permission to access this ticket.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Ticket not found. It may have been deleted or you may not have access to it.';
      } else if (error.response?.status >= 500) {
        errorMessage = 'Server error occurred. Please try again in a moment.';
      } else if (error.message.includes('Network Error') || error.message.includes('timeout')) {
        errorMessage = 'Network connection failed. Please check your connection and try again.';
      } else if (error.message.includes('Backend server is not responding')) {
        errorMessage = 'Backend server is not responding. Please ensure the server is running on port 3001 and try again.';
      } else if (error.message.includes('Invalid ticket data')) {
        errorMessage = 'Received invalid ticket data from server. Please refresh the page.';
      } else if (error.message.includes('Authentication failed') || error.message.includes('401')) {
        errorMessage = 'Authentication failed. Please log in again.';
      }
      
      console.error('❌ Setting error message:', errorMessage);
      setCreateError(errorMessage);
      setLoading(false);
      // Don't clear workflowLoadAttempted on error so we don't infinite loop
      // It will be reset when retryLoadTicketAndWorkflow is called
    }
  };



  const retryLoadTicketAndWorkflow = async () => {
    console.log('🔄 Retrying ticket and workflow loading...');
    setCreateError(null);
    setWorkflowLoadAttempted(null); // Reset so useEffect will retry
    await loadTicketDataAndCreateOrLoadWorkflow();
  };

  const loadWorkflow = async (preserveActiveStep: boolean = false) => {
    console.log('🚀 loadWorkflow function called with:', { ticketId, preserveActiveStep, loadingWorkflow });
    
    // Prevent concurrent loading
    if (loadingWorkflow) {
      console.log('⏳ Already loading workflow, skipping...');
      return;
    }

    setLoadingWorkflow(true);
    console.log('🔧 Set loadingWorkflow to true, starting workflow load process');
    
    try {
      console.log('🔍 Loading workflow for ticket:', ticketId);
      console.log('🔍 About to call API for service workflows...');
      
      // First try to get workflows for this ticket
      console.log('🔍 Making API call to:', `/tickets/${ticketId}/service-workflows`);
      const response = await apiService.get(`/tickets/${ticketId}/service-workflows`);
      console.log('✅ API call completed successfully');
      console.log('📥 Workflow response:', response);
      console.log('📊 Response type:', typeof response);
      console.log('📊 Response is array:', Array.isArray(response));
      console.log('📊 Response length:', response?.length);
      try {
        console.log('📊 Full response structure:', JSON.stringify(response, null, 2));
      } catch (jsonError) {
        console.log('📊 Could not stringify response:', jsonError);
        console.log('📊 Response object keys:', Object.keys(response || {}));
      }
      
      if (!response) {
        console.error('❌ No response from workflow API');
        throw new Error('No response received from workflow service');
      }
      
      // Handle different response formats that the backend might return
      let workflowsArray;
      if (Array.isArray(response)) {
        workflowsArray = response;
        console.log('📊 Response is array format');
      } else if (response.data && Array.isArray(response.data)) {
        workflowsArray = response.data;
        console.log('📊 Response is wrapped in data property');
      } else if (response.workflows && Array.isArray(response.workflows)) {
        workflowsArray = response.workflows;
        console.log('📊 Response has workflows property');
      } else {
        console.error('❌ Invalid workflow response format - could not find array:', typeof response);
        console.error('❌ Response keys:', Object.keys(response || {}));
        throw new Error('Invalid workflow data format received from server');
      }
      
      if (workflowsArray.length === 0) {
        console.log('ℹ️ No existing workflow found for ticket:', ticketId);
        setWorkflow(null);
        setActiveStep(1);
        setLoading(false);
        setLoadingWorkflow(false);
        setCreateError(null); // Clear any previous errors - no workflow is not an error condition
        return;
      }
      
      // Load existing workflow
      console.log('✅ Found existing workflow, loading details...');
      console.log('🔍 Workflow list response:', workflowsArray[0]);
      console.log('🔍 Available fields in first workflow:', Object.keys(workflowsArray[0]));
      
      // Extract workflow ID with fallback for different field naming conventions
      const workflowId = workflowsArray[0].workflow_id || workflowsArray[0].workflowId || workflowsArray[0].id;
      
      if (!workflowId) {
        console.error('❌ No workflow ID found in response:', workflowsArray[0]);
        console.error('🔍 All fields in response:', JSON.stringify(workflowsArray[0], null, 2));
        throw new Error('Workflow ID not found in server response');
      }
      
      console.log('🔍 Using workflow ID:', workflowId);
      
      // Get detailed workflow information
      console.log('🔍 Fetching detailed workflow data...');
      const workflowResponse = await apiService.get(`/service-workflows/${workflowId}`);
      console.log('📋 Workflow details:', workflowResponse);
      console.log('📋 Workflow details type:', typeof workflowResponse);
      console.log('📋 Workflow details fields:', Object.keys(workflowResponse || {}));
      try {
        console.log('📋 Full workflow response structure:', JSON.stringify(workflowResponse, null, 2));
      } catch (jsonError) {
        console.log('📋 Could not stringify workflow response:', jsonError);
        console.log('📋 Workflow response object keys:', Object.keys(workflowResponse || {}));
      }
      
      if (!workflowResponse) {
        console.error('❌ No workflow details received');
        throw new Error('Failed to load workflow details from server');
      }
      
      // Debug: Check if step definitions are present
      if (workflowResponse.steps && workflowResponse.steps.length > 0) {
        console.log('🔍 Step definitions check:');
        workflowResponse.steps.forEach((step: any, index: number) => {
          console.log(`  Step ${step.step_number}: has definition = ${!!step.definition}, fields = ${step.definition?.fields?.length || 0}`);
        });
      } else {
        console.warn('⚠️ No steps found in workflow response');
      }
      
      // Normalize field names to camelCase for consistent usage
      const workflow = {
        ...workflowResponse,
        workflowId: workflowResponse.workflowId || workflowResponse.workflow_id || workflowId,
        workflowNumber: workflowResponse.workflowNumber || workflowResponse.workflow_number,
        currentStep: workflowResponse.currentStep || workflowResponse.current_step || 1,
        deviceSerialNumber: workflowResponse.deviceSerialNumber || workflowResponse.device_serial_number,
        ticketId: workflowResponse.ticketId || workflowResponse.ticket_id,
        steps: workflowResponse.steps || []
      };
      setWorkflow(workflow);
      
      // Debug: Check normalized workflow
      console.log('🔍 Normalized workflow:', {
        workflowId: workflow.workflowId,
        currentStep: workflow.currentStep,
        stepsCount: workflow.steps?.length,
        firstStepHasDefinition: workflow.steps?.[0]?.definition ? true : false
      });
      
      // Set active step based on workflow's current step, not preserveActiveStep logic
      if (!preserveActiveStep) {
        const workflowCurrentStep = workflow.currentStep;
        console.log('🔄 WORKFLOW STEP ADVANCEMENT CHECK:');
        console.log('   Previous active step:', activeStep);
        console.log('   Workflow current step:', workflowCurrentStep);
        console.log('   Should advance?', workflowCurrentStep > activeStep);
        console.log('📋 Workflow data:', { 
          workflowId: workflow.workflowId, 
          currentStep: workflow.currentStep, 
          status: workflow.status,
          stepsCount: workflow.steps?.length 
        });
        setActiveStep(workflowCurrentStep);
        console.log('📍 Set active step to:', workflowCurrentStep);
        
        if (workflowCurrentStep > activeStep) {
          console.log('🎉 WORKFLOW ADVANCED! Moving from step', activeStep, 'to step', workflowCurrentStep);
        } else {
          console.log('⚠️ Workflow did not advance. Still on step', workflowCurrentStep);
        }
        
        // Initialize form data with current step data
        const currentStepData = workflow?.steps?.find((s: WorkflowStep) => (s.stepNumber || s.step_number) === workflowCurrentStep);
        if (currentStepData) {
          console.log('📋 Initializing form data with current step data:', currentStepData);
          setFormData(currentStepData);
        }
      }
      
      setLoading(false);
      setCreateError(null); // Clear any previous errors when workflow loads successfully
      console.log('✅ Workflow loaded successfully');
      
    } catch (error: any) {
      console.error('❌ Error loading workflow:', error);
      console.error('❌ Workflow error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        responseData: error.response?.data,
        url: error.config?.url,
        stack: error.stack
      });
      console.error('❌ Full error object:', error);
      
      // Re-throw the error with more context for the parent function to handle
      if (error.response?.status === 404) {
        throw new Error('Workflow not found for this ticket');
      } else if (error.response?.status === 500) {
        throw new Error('Server error while loading workflow data');
      } else if (error.message.includes('Network Error')) {
        throw new Error('Network connection failed while loading workflow');
      } else if (error.message.includes('Invalid workflow data format')) {
        throw new Error('Received invalid workflow data from server');
      } else {
        throw new Error(`Workflow loading failed: ${error.message}`);
      }
    } finally {
      setLoadingWorkflow(false);
    }
  };

  const loadAgents = async () => {
    try {
      const response = await apiService.getAgents();
      setAgents(response);
      console.log('🔍 Loaded agents for workflow:', response);
    } catch (error) {
      console.error('Error loading agents:', error);
    }
  };

  const createWorkflowInternal = async () => {
    console.log('🔧 Creating/loading workflow for ticket:', ticketId);

    try {
      // Get device serial number from props or use default
      const deviceSerialNumber = propDeviceSerial || 'TEMP-001';
      console.log('🔍 Using device serial number:', deviceSerialNumber);
      
      const response = await apiService.post(`/tickets/${ticketId}/service-workflows`, {
        deviceSerialNumber: deviceSerialNumber
      });

      console.log('✅ Workflow creation/loading response:', response);
      
      // Handle both new workflow creation and existing workflow return
      if (response && (response.workflow || response.success)) {
        const workflowData = response.workflow || response;
        console.log('🔍 Workflow data from response:', workflowData);
        
        // Debug: Check if step definitions are present in response
        if (workflowData.steps && workflowData.steps.length > 0) {
          console.log('🔍 Response step definitions check:');
          workflowData.steps.forEach((step: any, index: number) => {
            console.log(`  Step ${step.step_number}: has definition = ${!!step.definition}, fields = ${step.definition?.fields?.length || 0}`);
          });
        } else {
          console.warn('⚠️ No steps found in response');
        }
        
        // Normalize field names to camelCase for consistent usage
        const workflow = {
          ...workflowData,
          workflowId: workflowData.workflowId || workflowData.workflow_id,
          workflowNumber: workflowData.workflowNumber || workflowData.workflow_number,
          currentStep: workflowData.currentStep || workflowData.current_step || 1,
          deviceSerialNumber: workflowData.deviceSerialNumber || workflowData.device_serial_number,
          ticketId: workflowData.ticketId || workflowData.ticket_id,
          steps: workflowData.steps || []
        };
        
        console.log('🔍 Normalized workflow:', {
          workflowId: workflow.workflowId,
          currentStep: workflow.currentStep,
          stepsCount: workflow.steps?.length,
          firstStepHasDefinition: workflow.steps?.[0]?.definition ? true : false
        });
        
        setWorkflow(workflow);
        setCreateError(null);
        setLoading(false);
        
        if (response.message) {
          console.log('📝 Server message:', response.message);
        }
        
        console.log('🎉 Workflow loaded successfully:', workflow.workflowId);
        
        // Set active step and initialize form data
        const workflowCurrentStep = workflow.currentStep;
        setActiveStep(workflowCurrentStep);
        
        // Initialize form data with current step data
        const currentStepData = workflow?.steps?.find((s: WorkflowStep) => (s.stepNumber || s.step_number) === workflowCurrentStep);
        if (currentStepData) {
          console.log('🔍 Current step data:', currentStepData);
          console.log('🔍 Current step definition:', currentStepData.definition);
          setFormData(extractStepFormData(currentStepData));
        } else {
          console.warn('⚠️ No current step data found for step:', workflowCurrentStep);
        }
      } else {
        console.warn('⚠️ Unexpected response format:', response);
        throw new Error('Invalid workflow response format');
      }
      
    } catch (error: any) {
      console.error('❌ Error creating/loading workflow:', error);
      
      // Extract error message from response
      let errorMessage = 'Failed to create or load workflow';
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }

      // Since the backend should now return existing workflows instead of errors,
      // we shouldn't see the "already exists" error anymore. But keep fallback logic.
      if (errorMessage.includes('Service workflow already exists for this ticket') || 
          errorMessage.includes('already exists')) {
        console.log('🔄 Workflow already exists, trying alternative loading method...');
        try {
          // Try to load existing workflows using the direct API call
          await loadWorkflow();
          setCreateError(null);
          console.log('✅ Successfully loaded existing workflow via alternative method');
          return; // Don't show error if we successfully loaded existing workflow
        } catch (loadError) {
          console.error('❌ Failed to load existing workflow via alternative method:', loadError);
          errorMessage = 'Workflow exists but failed to load it. Please try refreshing the page.';
        }
      }

      setCreateError(errorMessage);
      setLoading(false);
      console.log('🔴 Final error message:', errorMessage);
      throw error; // Re-throw so calling function knows there was an error
    }
  };

  const createWorkflow = async () => {
    if (!user?.id || isCreating) {
      console.log('🚫 Cannot create workflow - user not authenticated or already creating');
      return;
    }

    setIsCreating(true);
    setCreateError(null);
    setWorkflowLoadAttempted(ticketId); // Mark that we've attempted to load for this ticket

    try {
      await createWorkflowInternal();
    } catch (error) {
      // Error handling is already done in createWorkflowInternal
      console.log('🔴 Workflow creation failed, error already handled');
    } finally {
      setIsCreating(false);
    }
  };

  const extractStepFormData = (step: WorkflowStep) => {
    const data: any = {};
    const stepNumber = step.stepNumber || step.step_number;
    
    console.log('📋 Extracting form data from step:', {
      stepNumber,
      stepName: step.stepName || step.step_name,
      hasDefinition: !!step.definition,
      fieldsCount: step.definition?.fields?.length || 0
    });
    
    // Extract all step-specific fields based on step definition
    if (step.definition && step.definition.fields) {
      step.definition.fields.forEach((field: any) => {
        let value = step[field.name];
        
        // Handle different data types
        if (value !== undefined && value !== null) {
          // Parse JSON strings for complex fields
          if (field.type === 'parts_table' || field.type === 'devices_table' || field.type === 'environment_conditions' || field.type === 'checklist') {
            try {
              if (typeof value === 'string' && value.trim() !== '') {
                value = JSON.parse(value);
              } else if (!value || (Array.isArray(value) && value.length === 0)) {
                value = (field.type === 'parts_table' || field.type === 'devices_table') ? [] : {};
              }
            } catch (e) {
              console.warn(`⚠️ Failed to parse JSON for field ${field.name}:`, value);
              value = (field.type === 'parts_table' || field.type === 'devices_table') ? [] : {};
            }
          }
          
          // Handle boolean fields
          if (field.type === 'boolean') {
            value = Boolean(value);
          }
          
          // Handle date fields
          if (field.type === 'date' && value) {
            // Ensure date is in proper format
            value = new Date(value).toISOString().split('T')[0];
          }
          
          data[field.name] = value;
          console.log(`📋 Extracted field ${field.name}:`, value);
        } else if (field.default) {
          // Apply default values
          let defaultValue = field.default;
          if (defaultValue === 'today') {
            defaultValue = new Date().toISOString().split('T')[0];
          }
          data[field.name] = defaultValue;
          console.log(`📋 Applied default for ${field.name}:`, defaultValue);
        } else {
          // Set appropriate empty values based on field type
          if (field.type === 'parts_table') {
            data[field.name] = [];
          } else if (field.type === 'checklist') {
            data[field.name] = {};
          } else if (field.type === 'boolean') {
            data[field.name] = false;
          } else {
            data[field.name] = '';
          }
        }
      });
    } else {
      console.warn('⚠️ No step definition or fields found for step:', stepNumber);
    }
    
    // For step 1, always use current ticket data for device fields (they are locked to ticket)
    if (stepNumber === 1 && (propTicket || ticket)) {
      const ticketData = propTicket || ticket;
      
      // Always override device fields in step 1 with current ticket data
      if (ticketData?.deviceSerialNumber) {
        data.deviceSerialNumber = ticketData.deviceSerialNumber;
        console.log('📋 Locked deviceSerialNumber from ticket:', ticketData.deviceSerialNumber);
      }
      
      if (ticketData?.deviceModel) {
        data.deviceModel = ticketData.deviceModel;
        console.log('📋 Locked deviceModel from ticket:', ticketData.deviceModel);
      }
    }
    
    console.log('📋 Final extracted form data keys:', Object.keys(data));
    return data;
  };

  const handleFormChange = (fieldName: string, value: any) => {
    // Prevent changes to locked fields in step 1
    const isStep1DeviceField = activeStep === 1 && (fieldName === 'deviceSerialNumber' || fieldName === 'deviceModel');
    if (isStep1DeviceField) {
      console.log(`🔒 Preventing change to locked field ${fieldName} in step 1`);
      return;
    }

    console.log(`🔄 handleFormChange called:`, {
      fieldName,
      valueType: typeof value,
      valueLength: Array.isArray(value) ? value.length : 'not array',
      value: Array.isArray(value) ? value : 'not array - ' + JSON.stringify(value).substring(0, 100)
    });

    setFormData((prev: any) => {
      const newFormData = {
        ...prev,
        [fieldName]: value
      };
      console.log(`📋 Form data updated for ${fieldName}:`, newFormData[fieldName]);
      return newFormData;
    });
    
    // Clear error for this field
    if (errors[fieldName]) {
      setErrors((prev: any) => ({
        ...prev,
        [fieldName]: null
      }));
    }
  };

  const validateForm = (stepDefinition: any) => {
    console.log('🚨 ====== FRONTEND VALIDATION DEBUG ======');
    console.log('🔍 Step definition:', stepDefinition);
    console.log('🔍 Current formData:', JSON.stringify(formData, null, 2));
    console.log('🔍 FormData keys:', Object.keys(formData));
    
    if (!stepDefinition || !stepDefinition.fields) {
      console.log('✅ No step definition or fields, validation passes');
      return { isValid: true, errors: {} };
    }
    
    console.log('🔍 Step definition fields:', stepDefinition.fields.map((f: any) => f.name));
    
    // TEMPORARILY DISABLE VALIDATION TO ALLOW SAVES FOR DEBUGGING
    console.log('🔧 FRONTEND VALIDATION TEMPORARILY DISABLED FOR DEBUGGING');
    return { isValid: true, errors: {} };
    
    /*
    const newErrors: any = {};
    
    stepDefinition.fields.forEach((field: any) => {
      const value = formData[field.name];
      
      console.log(`🔍 Checking field ${field.name}: value = "${value}", required = ${field.required}`);
      
      // Required field validation
      if (field.required === true && (!value || value === '')) {
        newErrors[field.name] = `${field.label} is required`;
        console.log(`❌ Field ${field.name} is required but empty`);
      }
      
      // Conditional validation
      if (field.required === 'conditional' && field.dependsOn) {
        const dependentValue = formData[field.dependsOn];
        if (field.dependsOnValue) {
          if (dependentValue === field.dependsOnValue && (!value || value === '')) {
            newErrors[field.name] = `${field.label} is required when ${field.dependsOn} is ${field.dependsOnValue}`;
          }
        } else if (dependentValue && (!value || value === '')) {
          newErrors[field.name] = `${field.label} is required when ${field.dependsOn} is set`;
        }
      }
    });
    
    const isValid = Object.keys(newErrors).length === 0;
    console.log('🔍 Validation result:', { isValid, errors: newErrors });
    return { isValid, errors: newErrors };
    */
  };

  const saveStep = async (markCompleted: boolean = false) => {
    console.log('🚨 ====== FRONTEND SAVE DEBUG ======');
    
    const currentStepObj = workflow?.steps.find((s: WorkflowStep) => 
      s.stepNumber === activeStep || s.step_number === activeStep
    );
    if (!currentStepObj) {
      console.error('❌ Cannot save step: no current step object found');
      return;
    }

    console.log('💾 Saving step:', {
      stepNumber: activeStep,
      stepName: currentStepObj.stepName || currentStepObj.step_name,
      markCompleted,
      formDataKeys: Object.keys(formData),
      formDataValues: formData
    });
    
    console.log('🔍 Complete formData object:', JSON.stringify(formData, null, 2));

    const stepDefinition = currentStepObj.definition;
    console.log('🔍 Step definition fields:', stepDefinition?.fields?.map((f: any) => f.name) || 'No fields');
    
    const validationResult = validateForm(stepDefinition);
    if (!validationResult.isValid) {
      console.error('❌ Validation failed:', validationResult.errors);
      setErrors(validationResult.errors);
      return;
    }
    console.log('✅ Frontend validation passed');

    const workflowId = workflow?.workflowId || workflow?.workflow_id;
    if (!workflowId) {
      console.error('❌ Cannot save step: no workflow ID');
      setErrors({ save: 'No workflow ID found' });
      return;
    }
    console.log('✅ Workflow ID found:', workflowId);

    // Get stepId with fallback for different field naming conventions
    const stepId = currentStepObj.stepId || currentStepObj.step_id;
    if (!stepId) {
      console.error('❌ Cannot save step: no step ID found', currentStepObj);
      setErrors({ save: 'No step ID found' });
      return;
    }
    console.log('✅ Step ID found:', stepId);

    try {
      setSaving(true);
      setErrors({}); // Clear previous errors
      
      const updateData = {
        ...formData,
        status: markCompleted ? 'completed' : 'in_progress'
      };

      console.log('📤 Final update data being sent to API:', JSON.stringify(updateData, null, 2));
      console.log('📤 Update data keys:', Object.keys(updateData));
      console.log('📤 API URL:', `/service-workflows/${workflowId}/steps/${stepId}`);

      console.log('📡 Making API call to save step...');
      const response = await apiService.put(`/service-workflows/${workflowId}/steps/${stepId}`, updateData);
      console.log('✅ Step saved successfully - Full response:', response);
      console.log('✅ Response data:', response?.data);
      console.log('✅ Response success:', response?.success);
      
      // Reload workflow to get updated currentStep
      console.log('🔄 Reloading workflow after step save...');
      await loadWorkflow(false);
      console.log('✅ Workflow reloaded successfully');
      
      setIsEditing(false);
      console.log('✅ Complete and Next operation finished successfully');
      
      // The workflow is automatically advanced on the backend when markCompleted is true
      // The loadWorkflow(false) call above will automatically set the activeStep to the 
      // workflow's current_step, so no manual navigation is needed
      
      // The workflow_updated socket event will be emitted by the backend
      // which will automatically update the TicketDetail component
      
    } catch (error: any) {
      console.error('❌ Error saving step:', error);
      console.error('❌ Error response:', error.response?.data);
      console.error('❌ Error status:', error.response?.status);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to save step';
      setErrors({ save: errorMessage });
    } finally {
      setSaving(false);
    }
  };

  const skipStep = async () => {
    const currentStepObj = workflow?.steps.find((s: WorkflowStep) => 
      s.stepNumber === activeStep || s.step_number === activeStep
    );
    if (!currentStepObj || !(currentStepObj.isOptional || currentStepObj.is_optional)) {
      console.error('❌ Cannot skip step: step is not optional or not found');
      return;
    }

    console.log('⏭️ Skipping step:', {
      stepNumber: activeStep,
      stepName: currentStepObj.stepName || currentStepObj.step_name
    });

    const workflowId = workflow?.workflowId || workflow?.workflow_id;
    if (!workflowId) {
      console.error('❌ Cannot skip step: no workflow ID');
      setErrors({ save: 'No workflow ID found' });
      return;
    }

    // Get stepId with fallback for different field naming conventions
    const stepId = currentStepObj.stepId || currentStepObj.step_id;
    if (!stepId) {
      console.error('❌ Cannot skip step: no step ID found', currentStepObj);
      setErrors({ save: 'No step ID found' });
      return;
    }

    try {
      setSaving(true);
      setErrors({}); // Clear previous errors
      
      console.log('📤 Sending skip request for step:', stepId);
      
      await apiService.put(`/service-workflows/${workflowId}/steps/${stepId}`, {
        status: 'skipped'
      });
      
      console.log('✅ Step skipped successfully');
      
      // Reload workflow to get updated currentStep
      await loadWorkflow(false);
      
      // Note: activeStep will be automatically updated from workflow.currentStep in loadWorkflow
      // The workflow_updated socket event will be emitted by the backend
      // which will automatically update the TicketDetail component
      
    } catch (error: any) {
      console.error('❌ Error skipping step:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to skip step';
      setErrors({ save: errorMessage });
    } finally {
      setSaving(false);
    }
  };

  // Load attachments for a step
  const loadStepAttachments = async (stepId: string) => {
    try {
      const stepAttachments = await apiService.get(`/service-workflows/steps/${stepId}/attachments`);
      setAttachments((prev: any) => ({
        ...prev,
        [stepId]: stepAttachments || []
      }));
    } catch (error) {
      console.error('Error loading step attachments:', error);
      setAttachments((prev: any) => ({
        ...prev,
        [stepId]: []
      }));
    }
  };

  // Handle file upload for a step
  const handleFileUpload = async (stepId: string, fieldName: string, files: FileList) => {
    const fileArray = Array.from(files);
    setUploadingFiles((prev: any) => ({
      ...prev,
      [stepId]: true
    }));

    const workflowId = workflow?.workflowId || workflow?.workflow_id;
    if (!workflowId) {
      console.error('❌ Cannot upload files: no workflow ID');
      setUploadingFiles((prev: any) => ({
        ...prev,
        [stepId]: false
      }));
      return;
    }

    console.log('📤 Starting file upload:', {
      stepId,
      fieldName,
      fileCount: fileArray.length,
      fileNames: fileArray.map(f => f.name),
      workflowId
    });

    try {
      const uploadedFiles: any[] = [];
      
      for (const file of fileArray) {
        console.log(`📤 Uploading file: ${file.name} (${file.size} bytes)`);
        
        const uploadFormData = new FormData();
        uploadFormData.append('attachment', file);

        // Use consistent backend URL construction
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
        const fullBackendUrl = backendUrl.startsWith('http') ? backendUrl : `https://${backendUrl}`;
        const response = await fetch(`${fullBackendUrl}/api/service-workflows/${workflowId}/steps/${stepId}/attachments`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          },
          body: uploadFormData
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error(`❌ Upload failed for ${file.name}:`, errorText);
          throw new Error(`Failed to upload ${file.name}: ${response.status} ${response.statusText}`);
        }

        const result = await response.json();
        console.log(`✅ File uploaded successfully: ${file.name}`, result);
        uploadedFiles.push(result.attachment);
      }

      console.log(`✅ All files uploaded successfully. Count: ${uploadedFiles.length}`);

      // Update attachments state
      setAttachments((prev: any) => ({
        ...prev,
        [stepId]: [...(prev[stepId] || []), ...uploadedFiles]
      }));

      // Update form data with attachment IDs (use correct field name)
      const currentAttachmentIds = formData[fieldName] || [];
      const newAttachmentIds = uploadedFiles.map((file: any) => file.attachment_id || file.attachmentId);
      handleFormChange(fieldName, [...currentAttachmentIds, ...newAttachmentIds]);

      console.log(`✅ Updated form data for field ${fieldName}:`, [...currentAttachmentIds, ...newAttachmentIds]);

    } catch (error) {
      console.error('❌ Error uploading files:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      alert(`Error uploading files: ${errorMessage}`);
    } finally {
      setUploadingFiles((prev: any) => ({
        ...prev,
        [stepId]: false
      }));
    }
  };

  // Download attachment
  const downloadAttachment = async (attachmentId: string, originalName: string) => {
    try {
      console.log('📥 Downloading attachment:', attachmentId);
      
      const token = localStorage.getItem('auth_token');
      if (!token) {
        throw new Error('No authentication token found. Please log in again.');
      }
      
      // Use consistent backend URL construction
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
      const fullBackendUrl = backendUrl.startsWith('http') ? backendUrl : `https://${backendUrl}`;
      const response = await fetch(`${fullBackendUrl}/api/service-workflows/attachments/${attachmentId}/download`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to download file: ${response.status} ${response.statusText}`);
      }

      // Create a blob from the response
      const blob = await response.blob();
      
      // Create a temporary URL for the blob
      const url = window.URL.createObjectURL(blob);
      
      // Create a temporary anchor element and trigger download
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = originalName;
      a.target = '_blank'; // Open in new tab as fallback
      document.body.appendChild(a);
      a.click();
      
      // Clean up
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }, 100);

      console.log('✅ Attachment downloaded successfully');

    } catch (error: any) {
      console.error('❌ Error downloading attachment:', error);
      const errorMessage = error.message || 'Unknown error occurred';
      alert(`❌ Error downloading attachment: ${errorMessage}`);
    }
  };

  // Remove attachment
  const removeAttachment = async (stepId: string, attachmentId: string, fieldName: string) => {
    try {
      await apiService.delete(`/service-workflows/attachments/${attachmentId}`);
      
      // Update attachments state
      setAttachments((prev: any) => ({
        ...prev,
        [stepId]: (prev[stepId] || []).filter((att: any) => att.attachmentId !== attachmentId)
      }));

      // Update form data
      const currentAttachmentIds = formData[fieldName] || [];
      handleFormChange(fieldName, currentAttachmentIds.filter((id: string) => id !== attachmentId));

    } catch (error) {
      console.error('Error removing attachment:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      alert(`Error removing attachment: ${errorMessage}`);
    }
  };

  const renderField = (field: any, optimized: boolean = false) => {
    const value = formData[field.name] || '';
    const error = errors[field.name];
    const isDisabled = !isEditing;

    // Check if this field should be locked to ticket data (step 1 device fields)
    const isStep1DeviceField = activeStep === 1 && (field.name === 'deviceSerialNumber' || field.name === 'deviceModel');
    const isFieldLocked = isStep1DeviceField;

    // Show/hide field based on dependencies
    if (field.dependsOn) {
      const dependentValue = formData[field.dependsOn];
      if (field.dependsOnValue && dependentValue !== field.dependsOnValue) {
        return null;
      }
      if (!field.dependsOnValue && !dependentValue) {
        return null;
      }
    }

    switch (field.type) {
      case 'text':
        return (
          <div key={field.name} className={optimized ? "mb-4" : "mb-3"}>
            <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} ${optimized ? 'mb-2' : 'mb-1'}`}>
              {field.label}
              {field.required === true && <span className="text-red-500 ml-1">*</span>}
              {isFieldLocked && (
                <span className={`ml-2 text-xs px-2 py-0.5 rounded ${isDarkMode ? 'bg-blue-900 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>
                  🔒 Locked to Ticket
                </span>
              )}
            </label>
            <input
              type="text"
              value={value}
              onChange={(e) => !isFieldLocked && handleFormChange(field.name, e.target.value)}
              disabled={isDisabled || isFieldLocked}
              className={`w-full px-3 ${optimized ? 'py-2.5' : 'py-2'} text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDisabled || isFieldLocked
                  ? isDarkMode ? 'bg-gray-800 cursor-not-allowed text-gray-400' : 'bg-gray-50 cursor-not-allowed' 
                  : isDarkMode ? 'bg-gray-800 text-white' : 'bg-white'
              } ${error ? 'border-red-500' : isDarkMode ? 'border-gray-600' : 'border-gray-300'}`}
            />
            {isFieldLocked && (
              <p className={`text-xs mt-1 ${isDarkMode ? 'text-blue-300' : 'text-blue-600'}`}>
                This field is automatically set from the ticket and cannot be edited here.
              </p>
            )}
            {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
          </div>
        );

      case 'textarea':
        return (
          <div key={field.name} className={optimized ? "mb-4" : "mb-3"}>
            <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} ${optimized ? 'mb-2' : 'mb-1'}`}>
              {field.label}
              {field.required === true && <span className="text-red-500 ml-1">*</span>}
            </label>
            <textarea
              value={value}
              onChange={(e) => handleFormChange(field.name, e.target.value)}
              disabled={isDisabled}
              rows={optimized ? 6 : 4}
              className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-vertical ${
                isDisabled 
                  ? isDarkMode ? 'bg-gray-800 cursor-not-allowed text-gray-400' : 'bg-gray-50 cursor-not-allowed' 
                  : isDarkMode ? 'bg-gray-800 text-white' : 'bg-white'
              } ${error ? 'border-red-500' : isDarkMode ? 'border-gray-600' : 'border-gray-300'}`}
              style={optimized ? { minHeight: '150px' } : { minHeight: '100px' }}
            />
            {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
          </div>
        );

      case 'date':
        return (
          <div key={field.name} className={optimized ? "mb-4" : "mb-3"}>
            <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} ${optimized ? 'mb-2' : 'mb-1'}`}>
              {field.label}
              {field.required === true && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              type="date"
              value={value ? value.split('T')[0] : ''}
              onChange={(e) => handleFormChange(field.name, e.target.value)}
              disabled={isDisabled}
              className={`w-full px-3 ${optimized ? 'py-2.5' : 'py-2'} text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDisabled 
                  ? isDarkMode ? 'bg-gray-800 cursor-not-allowed text-gray-400' : 'bg-gray-50 cursor-not-allowed' 
                  : isDarkMode ? 'bg-gray-800 text-white' : 'bg-white'
              } ${error ? 'border-red-500' : isDarkMode ? 'border-gray-600' : 'border-gray-300'}`}
            />
            {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
          </div>
        );

      case 'select':
        let options: any[] = [];
        if (field.options === 'agents') {
          console.log('🔍 Rendering agents dropdown:', agents);
          options = agents.map(agent => ({
            value: agent.id,
            label: `${agent.firstName} ${agent.lastName}`
          }));
          console.log('🔍 Agent options for dropdown:', options);
        } else if (Array.isArray(field.options)) {
          options = field.options;
        }

        return (
          <div key={field.name} className="mb-3">
            <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
              {field.label}
              {field.required === true && <span className="text-red-500 ml-1">*</span>}
              {isFieldLocked && (
                <span className={`ml-2 text-xs px-2 py-0.5 rounded ${isDarkMode ? 'bg-blue-900 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>
                  🔒 Locked to Ticket
                </span>
              )}
            </label>
            <select
              value={value}
              onChange={(e) => !isFieldLocked && handleFormChange(field.name, e.target.value)}
              disabled={isDisabled || isFieldLocked}
              className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDisabled || isFieldLocked
                  ? isDarkMode ? 'bg-gray-800 cursor-not-allowed text-gray-400' : 'bg-gray-50 cursor-not-allowed' 
                  : isDarkMode ? 'bg-gray-800 text-white' : 'bg-white'
              } ${error ? 'border-red-500' : isDarkMode ? 'border-gray-600' : 'border-gray-300'}`}
            >
              <option value="">Select {field.label}</option>
              {options.map((option, index) => (
                <option key={index} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {isFieldLocked && (
              <p className={`text-xs mt-1 ${isDarkMode ? 'text-blue-300' : 'text-blue-600'}`}>
                This field is automatically set from the ticket and cannot be edited here.
              </p>
            )}
            {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
          </div>
        );

      case 'boolean':
        return (
          <div key={field.name} className="mb-3">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={!!value}
                onChange={(e) => handleFormChange(field.name, e.target.checked)}
                disabled={isDisabled}
                className="mr-2 focus:ring-2 focus:ring-blue-500"
              />
              <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {field.label}
                {field.required === true && <span className="text-red-500 ml-1">*</span>}
              </span>
            </label>
            {field.labels && (
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mt-1`}>
                {value ? field.labels.true : field.labels.false}
              </p>
            )}
            {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
          </div>
        );

      case 'number':
        return (
          <div key={field.name} className={optimized ? "mb-4" : "mb-3"}>
            <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} ${optimized ? 'mb-2' : 'mb-1'}`}>
              {field.label}
              {field.required === true && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              type="number"
              value={value}
              onChange={(e) => handleFormChange(field.name, e.target.value)}
              disabled={isDisabled}
              className={`w-full px-3 ${optimized ? 'py-2.5' : 'py-2'} text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDisabled 
                  ? isDarkMode ? 'bg-gray-800 cursor-not-allowed text-gray-400' : 'bg-gray-50 cursor-not-allowed' 
                  : isDarkMode ? 'bg-gray-800 text-white' : 'bg-white'
              } ${error ? 'border-red-500' : isDarkMode ? 'border-gray-600' : 'border-gray-300'}`}
            />
            {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
          </div>
        );

      case 'parts_table':
        return renderPartsTable(field);

      case 'devices_table':
        return renderDevicesTable(field);

      case 'environment_conditions':
        return renderEnvironmentConditions(field);

      case 'checklist':
        return renderChecklist(field, optimized);

      case 'file':
        const currentStepObj = workflow?.steps.find((s: WorkflowStep) => s.stepNumber === activeStep);
        const stepId = currentStepObj?.stepId || currentStepObj?.step_id;
        const stepAttachments = attachments[stepId || ''] || [];
        const isUploading = uploadingFiles[stepId || ''] || false;

        return (
          <div key={field.name} className="mb-3">
            <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
              {field.label}
              {field.required === true && <span className="text-red-500 ml-1">*</span>}
            </label>
            
            {/* File Upload Component */}
            {isEditing && (
              <div className="mb-4">
                <FileUpload
                  onFileSelect={(files) => stepId && handleFileUpload(stepId, field.name, files)}
                  multiple={field.multiple || false}
                  disabled={isUploading}
                  accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx,.xls,.xlsx,.zip"
                  maxSize={200 * 1024 * 1024} // 200MB
                  className="mb-2"
                />
                {isUploading && (
                  <p className="text-blue-500 text-sm">Uploading files...</p>
                )}
              </div>
            )}

            {/* Display uploaded files */}
            {stepAttachments.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-600">Uploaded Files:</p>
                {stepAttachments.map((attachment: any) => (
                  <div
                    key={attachment.attachmentId}
                    className="flex items-center justify-between p-2 bg-gray-50 rounded border"
                  >
                    <div className="flex items-center space-x-2">
                      <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span className="text-sm text-gray-700">{attachment.originalName}</span>
                      <span className="text-xs text-gray-500">
                        ({(attachment.fileSize / 1024 / 1024).toFixed(2)} MB)
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => downloadAttachment(attachment.attachmentId, attachment.originalName)}
                        className="text-blue-500 hover:text-blue-700 text-sm"
                      >
                        Download
                      </button>
                      {isEditing && (
                        <button
                          onClick={() => stepId && removeAttachment(stepId, attachment.attachmentId, field.name)}
                          className="text-red-500 hover:text-red-700 text-sm"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
          </div>
        );

      default:
        return null;
    }
  };

  const renderPartsTable = (field: any) => {
    const parts = formData[field.name] || [];
    const error = errors[field.name];
    
    // Optional debug logging
    // console.log(`🔍 Rendering parts table:`, { fieldName: field.name, partsCount: parts.length });

    const addPart = () => {
      const newPart: any = {};
      field.schema.forEach((schemaField: any) => {
        newPart[schemaField.name] = '';
      });
      
      handleFormChange(field.name, [...parts, newPart]);
    };

    const updatePart = (index: number, partField: string, value: any) => {
      const updatedParts = [...parts];
      updatedParts[index][partField] = value;
      
      // If updating part number or part model, auto-fill part name from database
      if ((partField === 'partNumber' || partField === 'partModel') && value) {
        const selectedPart = availableParts.find(p => p.value === value);
        if (selectedPart) {
          // Extract part name from label (format: "SV1106 - Shipping and Handling")
          const partName = selectedPart.label.includes(' - ') 
            ? selectedPart.label.split(' - ').slice(1).join(' - ') // Handle cases with multiple dashes
            : selectedPart.label;
          updatedParts[index]['partName'] = partName;
          console.log(`🔄 Auto-filled part name: ${partName} for part number: ${value}`);
        }
      }
      
      handleFormChange(field.name, updatedParts);
    };

    const updatePartFromAutocomplete = (index: number, partField: string, partNumber: string, partName: string) => {
      console.log(`🎯 updatePartFromAutocomplete called:`, {
        index,
        partField,
        partNumber,
        partName,
        currentPart: parts[index],
        partsArrayLength: parts.length
      });
      
      const updatedParts = [...parts];
      
      // Log the part before update
      console.log(`📋 Part before update:`, updatedParts[index]);
      
      // Always update both partNumber and partName fields regardless of which field was clicked
      if (partField === 'partNumber' || partField === 'partModel') {
        // If user selected from part number or part model field, set the part number
        updatedParts[index]['partNumber'] = partNumber;
        updatedParts[index]['partModel'] = partNumber; // Also set partModel for consistency
      } else if (partField === 'partName') {
        // If user selected from part name field, set the part number too
        updatedParts[index]['partNumber'] = partNumber;
        updatedParts[index]['partModel'] = partNumber; // Also set partModel for consistency
      }
      
      // Always set the part name
      updatedParts[index]['partName'] = partName;
      
      // Log the part after update
      console.log(`📋 Part after update:`, updatedParts[index]);
      console.log(`🔄 Calling handleFormChange with:`, {
        fieldName: field.name,
        updatedPartsLength: updatedParts.length
      });
      
      handleFormChange(field.name, updatedParts);
    };

    const removePart = (index: number) => {
      const updatedParts = parts.filter((_: any, i: number) => i !== index);
      handleFormChange(field.name, updatedParts);
    };

    return (
      <div key={field.name} className="mb-3">
        <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
          {field.label}
          {field.required === true && <span className="text-red-500 ml-1">*</span>}
        </label>
        
        <div className={`border rounded-md ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`} style={{ minHeight: '320px' }}>
          {parts.length === 0 ? (
            <div className={`p-4 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} style={{ paddingTop: '100px', paddingBottom: '100px' }}>
              No parts added yet
            </div>
          ) : (
            <div className="overflow-x-auto" style={{ minHeight: '280px', paddingBottom: '40px' }}>
              <table className="w-full">
                <thead className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <tr>
                    {field.schema.map((schemaField: any) => (
                      <th key={schemaField.name} className={`px-4 py-2 text-left text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {schemaField.label}
                      </th>
                    ))}
                    <th className={`px-4 py-2 text-left text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {parts.map((part: any, index: number) => (
                    <tr key={index} className="border-t">
                      {field.schema.map((schemaField: any) => (
                        <td key={schemaField.name} className="px-4 py-2">
                          {schemaField.name === 'partNumber' ? (
                            <PartsAutocomplete
                              value={part[schemaField.name] || ''}
                              onChange={(partNumber, partName) => {
                                console.log(`🎯 partNumber field onChange:`, { index, partNumber, partName });
                                updatePartFromAutocomplete(index, schemaField.name, partNumber, partName);
                              }}
                              placeholder={loadingParts ? 'Loading parts...' : 'Type part number...'}
                              disabled={!isEditing || loadingParts}
                              availableParts={availableParts}
                              searchBy="partNumber"
                            />
                          ) : schemaField.name === 'partModel' ? (
                            <PartsAutocomplete
                              value={part[schemaField.name] || ''}
                              onChange={(partNumber, partName) => {
                                console.log(`🎯 partModel field onChange:`, { index, partNumber, partName });
                                updatePartFromAutocomplete(index, schemaField.name, partNumber, partName);
                              }}
                              placeholder={loadingParts ? 'Loading parts...' : 'Type part model...'}
                              disabled={!isEditing || loadingParts}
                              availableParts={availableParts}
                              searchBy="partNumber"
                            />
                          ) : schemaField.name === 'partName' ? (
                            <PartsAutocomplete
                              value={part[schemaField.name] || ''}
                              onChange={(partNumber, partName) => {
                                console.log(`🎯 partName field onChange:`, { index, partNumber, partName });
                                updatePartFromAutocomplete(index, schemaField.name, partNumber, partName);
                              }}
                              placeholder={part['partNumber'] ? 'Auto-filled from part selection' : 'Type part name...'}
                              disabled={!isEditing || loadingParts}
                              availableParts={availableParts}
                              searchBy="partName"
                            />
                          ) : (
                            <input
                              type={schemaField.type}
                              value={part[schemaField.name] || ''}
                              onChange={(e) => updatePart(index, schemaField.name, e.target.value)}
                              disabled={!isEditing}
                              title={part[schemaField.name] && part[schemaField.name].trim() ? part[schemaField.name] : undefined}
                              className="w-full px-2 py-1 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                          )}
                        </td>
                      ))}
                      <td className="px-4 py-2">
                        {isEditing && (
                          <button
                            onClick={() => removePart(index)}
                            className="text-red-500 hover:text-red-700 text-sm"
                          >
                            Remove
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        
        {isEditing && (
          <button
            onClick={addPart}
            className="mt-2 px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600"
          >
            Add {field.label.slice(0, -1)}
          </button>
        )}
        
        {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
      </div>
    );
  };

  const renderDevicesTable = (field: any) => {
    const devices = formData[field.name] || [];
    const error = errors[field.name];
    
    const addDevice = () => {
      const newDevice: any = {};
      field.schema.forEach((schemaField: any) => {
        if (schemaField.type === 'boolean') {
          newDevice[schemaField.name] = false;
        } else {
          newDevice[schemaField.name] = '';
        }
      });
      
      handleFormChange(field.name, [...devices, newDevice]);
    };

    const updateDevice = (index: number, deviceField: string, value: any) => {
      const updatedDevices = [...devices];
      updatedDevices[index][deviceField] = value;
      handleFormChange(field.name, updatedDevices);
    };

    const removeDevice = (index: number) => {
      const updatedDevices = devices.filter((_: any, i: number) => i !== index);
      handleFormChange(field.name, updatedDevices);
    };

    return (
      <div key={field.name} className="mb-3">
        <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
          {field.label}
          {field.required === true && <span className="text-red-500 ml-1">*</span>}
        </label>
        
        <div className={`border rounded-md ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`} style={{ minHeight: '280px' }}>
          {devices.length === 0 ? (
            <div className={`p-3 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} style={{ paddingTop: '60px', paddingBottom: '60px' }}>
              <div className="text-sm">No devices added yet</div>
            </div>
          ) : (
            <div className="overflow-x-auto" style={{ minHeight: '240px', paddingBottom: '30px' }}>
              <table className="w-full">
                <thead className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <tr>
                    {field.schema.map((schemaField: any) => (
                      <th key={schemaField.name} className={`px-2 py-1.5 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {schemaField.label}
                      </th>
                    ))}
                    <th className={`px-2 py-1.5 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {devices.map((device: any, index: number) => (
                    <tr key={index} className="border-t">
                      {field.schema.map((schemaField: any) => (
                        <td key={schemaField.name} className="px-2 py-1.5">
                          {schemaField.type === 'boolean' ? (
                            <input
                              type="checkbox"
                              checked={device[schemaField.name] || false}
                              onChange={(e) => updateDevice(index, schemaField.name, e.target.checked)}
                              disabled={!isEditing}
                              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                            />
                          ) : (
                            <input
                              type="text"
                              value={device[schemaField.name] || ''}
                              onChange={(e) => updateDevice(index, schemaField.name, e.target.value)}
                              disabled={!isEditing}
                              title={device[schemaField.name] && device[schemaField.name].trim() ? device[schemaField.name] : undefined}
                              className="w-full px-1.5 py-1 border rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                          )}
                        </td>
                      ))}
                      <td className="px-2 py-1.5">
                        {isEditing && (
                          <button
                            onClick={() => removeDevice(index)}
                            className="text-red-500 hover:text-red-700 text-xs"
                          >
                            Remove
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        
        {isEditing && (
          <button
            onClick={addDevice}
            className="mt-2 px-2 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600"
          >
            Add Device
          </button>
        )}
        
        {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
      </div>
    );
  };

  const renderEnvironmentConditions = (field: any) => {
    const environmentData = formData[field.name] || {};
    const error = errors[field.name];

    const updateEnvironmentField = (fieldName: string, value: any) => {
      const updatedData = {
        ...environmentData,
        [fieldName]: value
      };
      handleFormChange(field.name, updatedData);
    };

    return (
      <div key={field.name} className="mb-3">
        <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
          {field.label}
          {field.required === true && <span className="text-red-500 ml-1">*</span>}
        </label>
        
        <div className={`border rounded-md p-4 ${isDarkMode ? 'border-gray-600 bg-gray-800' : 'border-gray-300 bg-gray-50'}`}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {field.schema.map((schemaField: any) => (
              <div key={schemaField.name}>
                <label className={`block text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mb-1`}>
                  {schemaField.label}
                  {schemaField.required && <span className="text-red-500 ml-1">*</span>}
                </label>
                
                {schemaField.type === 'select' ? (
                  <select
                    value={environmentData[schemaField.name] || ''}
                    onChange={(e) => updateEnvironmentField(schemaField.name, e.target.value)}
                    disabled={!isEditing}
                    className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                      isDarkMode 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  >
                    <option value="">Select {schemaField.label}</option>
                    {schemaField.options?.map((option: any) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type={schemaField.type}
                    value={environmentData[schemaField.name] || ''}
                    onChange={(e) => updateEnvironmentField(schemaField.name, schemaField.type === 'number' ? parseFloat(e.target.value) || '' : e.target.value)}
                    placeholder={schemaField.placeholder || ''}
                    min={schemaField.min}
                    max={schemaField.max}
                    disabled={!isEditing}
                    className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                      isDarkMode 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
        
        {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
      </div>
    );
  };

  const renderChecklist = (field: any, optimized: boolean = false) => {
    const checklist = formData[field.name] || {};
    const error = errors[field.name];

    return (
      <div key={field.name} className={optimized ? "mb-4" : "mb-3"}>
        <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} ${optimized ? 'mb-3' : 'mb-2'}`}>
          {field.label}
          {field.required === true && <span className="text-red-500 ml-1">*</span>}
        </label>
        
        <div className={`space-y-2 border rounded-md p-3 ${isDarkMode ? 'border-gray-600 bg-gray-800' : 'border-gray-300 bg-white'}`} style={optimized ? { minHeight: '200px', paddingTop: '24px', paddingBottom: '24px' } : undefined}>
          {field.items.map((item: any) => (
            <label key={item.name} className="flex items-center">
              <input
                type="checkbox"
                checked={!!checklist[item.name]}
                onChange={(e) => handleFormChange(field.name, {
                  ...checklist,
                  [item.name]: e.target.checked
                })}
                disabled={!isEditing}
                className="mr-2 focus:ring-2 focus:ring-blue-500"
              />
              <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{item.label}</span>
            </label>
          ))}
        </div>
        
        {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
      </div>
    );
  };

  const handleStepClick = (stepNumber: number) => {
    if (!isEditing) {
      setActiveStep(stepNumber);
      const stepData = workflow?.steps.find((s: WorkflowStep) => (s.stepNumber || s.step_number) === stepNumber);
      if (stepData) {
        setFormData(extractStepFormData(stepData));
      }
    }
  };

  const startEditing = () => {
    setIsEditing(true);
    const currentStepObj = workflow?.steps.find((s: WorkflowStep) => (s.stepNumber || s.step_number) === activeStep);
    if (currentStepObj) {
      setFormData(extractStepFormData(currentStepObj));
    }
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setErrors({});
    const currentStepObj = workflow?.steps.find((s: WorkflowStep) => (s.stepNumber || s.step_number) === activeStep);
    if (currentStepObj) {
      setFormData(extractStepFormData(currentStepObj));
    }
  };

  // Function to get detailed step instructions
  const getStepInstructions = (stepNumber: number): string => {
    const instructions = {
      1: "📦 Start the repair process by confirming the device serial number, collecting a description of the issue, and entering the shipping tracking number provided by the customer.",
      2: "🚚 If a loaner device will be sent, enter the model and serial number of the loaner, as well as the tracking information for the shipment. Skip this step if no loaner is being provided.",
      3: "🧼 Record when the device arrived. Log any components received, inspect the unit's condition, confirm the product ID, and record the cleaning date.",
      4: "🔍 Analyze the device to identify the root cause of the issue. Record the defect findings and list any parts that will need to be replaced.\n🛑 For technical specifications of components and diagnostics, please refer to the Device Master Records:\nhttps://netorg277030.sharepoint.com/sites/NeurovirtualTechnicalFiles",
      5: "💰 Send the customer a repair quote and wait for approval. Log the quote number, date sent, and approval date. If applicable, also include the invoice number.",
      6: "🛠️ Describe the repairs performed and confirm the list of replaced parts. Complete the functional checklist to verify that the device passed all tests.\n🛑 Refer to the Device Master Records for assembly procedures, required materials, and test specifications:\nhttps://netorg277030.sharepoint.com/sites/NeurovirtualTechnicalFiles",
      7: "✅ Review all information and testing results. If everything is correct, approve the service. This step must be done by a different agent from the repair technician.",
      8: "📤 Ship the repaired device back to the customer. Record the shipment date and tracking number, and add any relevant notes about the return.",
      9: "☎️ Contact the customer to confirm the device was received and is working properly. Record the contact person's name and any customer feedback.",
      10: "📦 Once the customer has returned the loaner device, record the tracking number and date it was received back, along with the responsible agent."
    };
    
    return instructions[stepNumber as keyof typeof instructions] || "";
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading service workflow...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show authentication error if not authenticated
  if (!isAuthenticated && !loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Authentication Required
            </h3>
            <p className="text-gray-600 mb-6">
              You must be logged in to access service workflows.
            </p>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Only show "No Service Workflow Found" dialog if we're not loading and there's no workflow
  if (!loading && !loadingWorkflow && (!workflow || !workflow.steps)) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-md w-full mx-4`}>
          <div className="text-center">
            <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'} mb-4`}>
              No Service Workflow Found
            </h3>
            <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'} mb-6`}>
              This ticket doesn't have a service workflow yet. Would you like to create one?
            </p>
            {createError && (
              <div className={`${isDarkMode ? 'bg-red-900 border-red-700 text-red-300' : 'bg-red-100 border-red-400 text-red-700'} border px-4 py-3 rounded mb-4`}>
                <p className="text-sm">{createError}</p>
                <div className="mt-3 flex justify-center space-x-2">
                  <button
                    onClick={retryLoadTicketAndWorkflow}
                    disabled={loading}
                    className={`px-3 py-1 text-xs font-medium rounded ${
                      isDarkMode 
                        ? 'bg-red-600 hover:bg-red-500 text-white disabled:bg-red-800' 
                        : 'bg-red-600 hover:bg-red-500 text-white disabled:bg-red-400'
                    } transition-colors disabled:cursor-not-allowed`}
                  >
                    {loading ? 'Retrying...' : 'Retry'}
                  </button>
                  <button
                    onClick={() => setCreateError(null)}
                    className={`px-3 py-1 text-xs font-medium rounded ${
                      isDarkMode 
                        ? 'bg-gray-600 hover:bg-gray-500 text-white' 
                        : 'bg-gray-500 hover:bg-gray-400 text-white'
                    } transition-colors`}
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            )}
            <div className="flex space-x-3 justify-center">
              <button
                onClick={onClose}
                className={`px-4 py-2 ${isDarkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-800'} transition-colors`}
              >
                Cancel
              </button>
              <button
                onClick={createWorkflow}
                disabled={isCreating}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors disabled:opacity-50"
              >
                {isCreating ? 'Creating...' : 'Create Workflow'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Get current step object
  const currentStepObj = workflow?.steps?.find((s: WorkflowStep) => 
    s.stepNumber === activeStep || s.step_number === activeStep
  );

  console.log('🔍 ServiceWorkflow debug:', {
    activeStep,
    workflowSteps: workflow?.steps?.length || 0,
    currentStepObj: currentStepObj ? 'found' : 'not found',
    currentStepNumber: currentStepObj?.stepNumber || currentStepObj?.step_number,
    hasDefinition: !!currentStepObj?.definition,
    fieldsCount: currentStepObj?.definition?.fields?.length || 0
  });

  const canEdit = currentStepObj && (
    currentStepObj.status === 'in_progress' || 
    currentStepObj.status === 'not_started' || 
    currentStepObj.status === 'completed' ||
    (currentStepObj.status === 'skipped' && ((currentStepObj.stepNumber || currentStepObj.step_number) === 2 || (currentStepObj.stepNumber || currentStepObj.step_number) === 10))
  );
  const isCompleted = currentStepObj?.status === 'completed';
  const isSkipped = currentStepObj?.status === 'skipped';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-2">
      <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-4 w-full max-w-[98vw] xl:max-w-[95vw] 2xl:max-w-[90vw] max-h-[95vh] overflow-y-auto`}>
        <div className="flex justify-between items-center mb-4">
          <div className="flex-1">
            <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'} mb-1`}>
              Service Workflow - {workflow.workflowNumber}
            </h2>
            <div className="flex flex-wrap gap-4 text-sm">
              <span className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Device: {workflow.deviceSerialNumber}
              </span>
              <span className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Ticket: {ticket?.ticketNumber}
              </span>
              <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Status: <span className="capitalize">{workflow.status}</span>
              </span>
              <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Step: {workflow.currentStep || 1} / {workflow?.steps?.length || 10}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            className={`${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-600'} transition-colors flex-shrink-0 ml-4`}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Wizard Stepper */}
        <div className="mb-6">
          <div className="w-full px-2">
            <div className="flex items-stretch w-full min-h-[70px] wizard-stepper-container">
              {workflow?.steps?.map((step: WorkflowStep, index: number) => {
                const isActive = (step.stepNumber || step.step_number) === activeStep;
                const isCompleted = step.status === 'completed';
                const isSkipped = step.status === 'skipped';
                const isInProgress = step.status === 'in_progress';
                const isLast = index === workflow.steps.length - 1;
                const isFirst = index === 0;
                
                return (
                  <div 
                    key={step.stepNumber || step.step_number} 
                    className="flex items-stretch flex-1"
                  >
                    <div className="relative w-full">
                      <button
                        onClick={() => handleStepClick(step.stepNumber || step.step_number || 1)}
                        className={`
                          relative flex items-center px-2 py-2 text-xs font-medium transition-all duration-200 cursor-pointer
                          w-full min-h-[70px] shadow-sm
                          ${isCompleted 
                            ? 'bg-green-500 text-white hover:bg-green-600' 
                            : isActive || isInProgress
                            ? 'bg-blue-500 text-white hover:bg-blue-600'
                            : isSkipped
                            ? 'bg-gray-400 text-white hover:bg-gray-500'
                            : isDarkMode
                            ? 'bg-gray-600 text-gray-200 hover:bg-gray-500'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          }
                          ${!isLast ? 'clip-path-chevron' : 'rounded-r-md'}
                          ${isFirst ? 'rounded-l-md' : ''}
                        `}
                        style={{
                          clipPath: !isLast 
                            ? 'polygon(0 0, calc(100% - 16px) 0, 100% 50%, calc(100% - 16px) 100%, 0 100%, 16px 50%)'
                            : !isFirst 
                            ? 'polygon(16px 0, 100% 0, 100% 100%, 16px 100%, 0 50%)'
                            : 'none',
                          marginLeft: !isFirst ? '-1px' : '0'
                        }}
                      >
                        <div className="flex items-center relative z-10 w-full">
                          {/* Step Content - Centered with proper spacing for chevron */}
                          <div className="text-center flex-1 px-2">
                            <div 
                              className="font-medium text-xs leading-tight break-words hyphens-auto"
                              style={{ 
                                overflowWrap: 'break-word',
                                wordBreak: 'break-word',
                                lineHeight: '1.3',
                                marginRight: !isLast ? '16px' : '0',
                                maxWidth: '100%'
                              }}
                              title={step.stepName || step.step_name}
                            >
                              {step.stepName || step.step_name}
                            </div>
                          </div>
                          
                          {/* Step Number Circle - Positioned absolutely to avoid layout conflicts */}
                          <div 
                            className={`
                              absolute top-1/2 transform -translate-y-1/2 
                              flex items-center justify-center w-5 h-5 rounded-full text-xs font-bold
                              ${isCompleted || isActive || isInProgress || isSkipped
                                ? 'bg-white'
                                : isDarkMode ? 'bg-gray-700' : 'bg-white'
                              }
                              ${isCompleted 
                                ? 'text-green-500' 
                                : isActive || isInProgress
                                ? 'text-blue-500'
                                : isSkipped
                                ? 'text-gray-400'
                                : isDarkMode ? 'text-gray-300' : 'text-gray-600'
                              }
                            `}
                            style={{
                              right: !isLast ? '4px' : '4px'
                            }}
                          >
                            {isCompleted ? (
                              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            ) : isSkipped ? (
                              <span>-</span>
                            ) : (
                              step.stepNumber || step.step_number
                            )}
                          </div>
                        </div>
                      </button>
                      
                      {/* Optional Badge */}
                                                {(step.isOptional || step.is_optional) && (
                        <div className="absolute -top-1 -right-1 bg-yellow-400 text-yellow-900 text-[9px] px-1 py-0.5 rounded-full font-medium z-20">
                          Opt
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Compact Step Header - Condensed instructions */}
        <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-lg p-3 mb-4`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex-1 min-w-0">
              <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'} mb-1 truncate`}>
                Step {activeStep}: {currentStepObj?.stepName || currentStepObj?.step_name}
              </h3>
              <div className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'} text-sm`}>
                {currentStepObj?.stepDescription || currentStepObj?.step_description}
              </div>
            </div>
            
            <div className="flex items-center gap-3 flex-shrink-0">
              {/* Status indicators - Compact */}
              {isCompleted && (
                <div className={`${isDarkMode ? 'bg-green-900 border-green-700 text-green-300' : 'bg-green-100 border-green-400 text-green-700'} border px-2 py-1 rounded text-xs font-medium`}>
                  ✓ Completed
                </div>
              )}
              
              {isSkipped && (
                <div className={`${isDarkMode ? 'bg-yellow-900 border-yellow-700 text-yellow-300' : 'bg-yellow-100 border-yellow-400 text-yellow-700'} border px-2 py-1 rounded text-xs font-medium`}>
                  ⤴ Skipped
                </div>
              )}

              {/* Compact Instructions Toggle */}
              <details className="group">
                <summary className={`${isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'} cursor-pointer text-xs font-medium px-2 py-1 rounded border border-current hover:bg-opacity-10 hover:bg-current transition-colors`}>
                  📋 Instructions
                </summary>
                <div className={`absolute z-10 right-0 mt-1 w-96 max-w-[90vw] ${isDarkMode ? 'bg-blue-900 border-blue-700 text-blue-200' : 'bg-blue-50 border-blue-200 text-gray-700'} border rounded-lg p-3 text-sm shadow-lg`}>
                  <div className="whitespace-pre-line max-h-64 overflow-y-auto">
                    {getStepInstructions(activeStep).split('\n').map((line, index) => (
                      <div key={index} className="mb-1 last:mb-0 text-xs">
                        {line.includes('https://') ? (
                          <span>
                            {line.split('https://')[0]}
                            <a 
                              href={`https://${line.split('https://')[1]}`}
                              target="_blank" 
                              rel="noopener noreferrer"
                              className={`${isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'} underline`}
                            >
                              https://{line.split('https://')[1]}
                            </a>
                          </span>
                        ) : (
                          line
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </details>
            </div>
          </div>
        </div>

        {/* Form Section - Full Width */}
        <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-white'} rounded border p-4 min-h-[400px] flex flex-col`}>
          {/* Action Buttons - Streamlined layout */}
          <div className="flex flex-wrap gap-2 mb-4 min-h-[40px] items-start">
            {canEdit && !isEditing && (
              <button
                onClick={startEditing}
                className="px-4 py-2 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 transition-colors"
              >
                ✏️ Edit Step
              </button>
            )}
            
            {isEditing && (
              <>
                <button
                  onClick={cancelEditing}
                  className={`px-3 py-2 text-sm border rounded ${isDarkMode ? 'border-gray-600 text-gray-300 hover:text-white hover:border-gray-500' : 'border-gray-300 text-gray-600 hover:text-gray-800 hover:border-gray-400'} transition-colors`}
                >
                  Cancel
                </button>
                <button
                  onClick={() => saveStep(false)}
                  disabled={saving}
                  className="px-4 py-2 bg-green-500 text-white text-sm rounded hover:bg-green-600 transition-colors disabled:opacity-50"
                >
                  {saving ? 'Saving...' : '💾 Save'}
                </button>
                <button
                  onClick={() => saveStep(true)}
                  disabled={saving}
                  className="px-4 py-2 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 transition-colors disabled:opacity-50"
                >
                  {saving ? 'Completing...' : '✅ Complete & Next'}
                </button>
                {(currentStepObj?.isOptional || currentStepObj?.is_optional) && (
                  <button
                    onClick={skipStep}
                    disabled={saving}
                    className="px-4 py-2 bg-gray-500 text-white text-sm rounded hover:bg-gray-600 transition-colors disabled:opacity-50"
                  >
                    {saving ? 'Skipping...' : '⏭️ Skip'}
                  </button>
                )}
              </>
            )}
          </div>

          {/* Step Form Fields - Enhanced layout with full width utilization */}
          <div className="flex-1">
                {loadingWorkflow ? (
                  <div className="flex items-center justify-center h-32">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                      <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'} text-sm`}>
                        Loading step definitions...
                      </p>
                    </div>
                  </div>
                ) : currentStepObj?.definition?.fields ? (
                  (() => {
                    const fields = currentStepObj.definition.fields;
                    
                    // Enhanced layout for Step 4 - Organized layout with diagnostic next to responsible
                    if (activeStep === 4) {
                      const analysisDateField = fields.find((f: any) => 
                        f.name === 'analysisDate' || f.name === 'analyzeDate' || f.name === 'date' ||
                        f.label?.toLowerCase().includes('analysis') && f.type === 'date'
                      );
                      const responsibleField = fields.find((f: any) => 
                        f.name === 'responsible' || f.name === 'responsibleAgent' || 
                        f.label?.toLowerCase().includes('responsible')
                      );
                      const diagnosticField = fields.find((f: any) => 
                        f.name === 'diagnostic' || f.name === 'diagnosticResults' || f.name === 'diagnosticSummary' ||
                        f.label?.toLowerCase().includes('diagnostic')
                      );
                      const findingsField = fields.find((f: any) => 
                        f.name === 'findings' || f.name === 'defectFindings' || f.name === 'findingsDescription' || 
                        f.label?.toLowerCase().includes('findings')
                      );
                      const commentsField = fields.find((f: any) => 
                        f.name === 'comments' || f.name === 'notes' ||
                        f.label?.toLowerCase().includes('comment')
                      );
                      const attachmentsField = fields.find((f: any) => 
                        f.type === 'file' || f.name === 'attachments' ||
                        f.label?.toLowerCase().includes('attachment')
                      );
                      const partsField = fields.find((f: any) => 
                        f.name === 'partsToReplace' || f.name === 'replacementParts' ||
                        f.label?.toLowerCase().includes('replacement') || f.label?.toLowerCase().includes('parts')
                      );
                      const otherFields = fields.filter((f: any) => 
                        f !== analysisDateField && f !== responsibleField && f !== diagnosticField && 
                        f !== findingsField && f !== commentsField && f !== attachmentsField && f !== partsField
                      );
                      
                      return (
                        <div className="space-y-6">
                          {/* Other fields first - 3-column responsive grid */}
                          {otherFields.length > 0 && (
                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                              {otherFields.map((field: any) => renderField(field, true))}
                            </div>
                          )}
                          
                          {/* First level - Analysis Date, Responsible Agent, and Diagnostic */}
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {/* Analysis Date - Left column */}
                            {analysisDateField && (
                              <div className="w-full">
                                {renderField(analysisDateField, true)}
                              </div>
                            )}
                            
                            {/* Responsible Agent - Center column */}
                            {responsibleField && (
                              <div className="w-full">
                                {renderField(responsibleField, true)}
                              </div>
                            )}
                            
                            {/* Diagnostic Results - Right column */}
                            {diagnosticField && (
                              <div className="w-full">
                                {renderField(diagnosticField, true)}
                              </div>
                            )}
                          </div>
                          
                          {/* Findings section - Full width */}
                          {findingsField && (
                            <div className="w-full">
                              {renderField(findingsField, true)}
                            </div>
                          )}
                          
                          {/* Parts to replace - Full width table */}
                          {partsField && (
                            <div className="w-full">
                              {renderField(partsField)}
                            </div>
                          )}
                          
                          {/* Comments and Attachments - Side by side below parts */}
                          {(commentsField || attachmentsField) && (
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                              {/* Comments - Left column */}
                              {commentsField && (
                                <div className="w-full">
                                  {renderField(commentsField, true)}
                                </div>
                              )}
                              
                              {/* Attachments - Right column */}
                              {attachmentsField && (
                                <div className="w-full">
                                  {renderField(attachmentsField, true)}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    }
                    
                    // Enhanced layout for Step 6 - Organized layout with specific positioning
                    if (activeStep === 6) {
                      const responsibleField = fields.find((f: any) => 
                        f.name === 'responsible' || f.name === 'responsibleAgent' || 
                        f.label?.toLowerCase().includes('responsible')
                      );
                      const environmentField = fields.find((f: any) => 
                        f.name === 'environmentConditions' || f.name === 'environment' ||
                        f.label?.toLowerCase().includes('environment')
                      );
                      const correctionDateField = fields.find((f: any) => 
                        f.name === 'correctionDate' || f.name === 'repairDate' || f.name === 'completionDate' ||
                        f.label?.toLowerCase().includes('correction') || f.label?.toLowerCase().includes('repair date')
                      );
                      const repairDescriptionField = fields.find((f: any) => 
                        f.name === 'repairDescription' || f.name === 'repairWork' || f.name === 'repairs' ||
                        f.label?.toLowerCase().includes('repair') && f.type === 'textarea'
                      );
                      const testChecklistField = fields.find((f: any) => 
                        f.name === 'testChecklist' || f.name === 'checklist' ||
                        f.label?.toLowerCase().includes('test') && f.type === 'checklist'
                      );
                      const devicesField = fields.find((f: any) => 
                        f.name === 'devicesUsed' || f.name === 'devices' ||
                        f.label?.toLowerCase().includes('devices')
                      );
                      const finalApprovalField = fields.find((f: any) => 
                        f.name === 'finalRepairApproval' || f.name === 'finalApproval' ||
                        f.label?.toLowerCase().includes('final') && f.label?.toLowerCase().includes('approval')
                      );
                      const partsUsedField = fields.find((f: any) => 
                        f.name === 'partsUsed' || f.name === 'parts' ||
                        f.label?.toLowerCase().includes('parts') && f.type === 'parts_table'
                      );
                      const commentsField = fields.find((f: any) => 
                        f.name === 'comments' || f.name === 'notes' ||
                        f.label?.toLowerCase().includes('comment')
                      );
                      const attachmentsField = fields.find((f: any) => 
                        f.type === 'file' || f.name === 'attachments' ||
                        f.label?.toLowerCase().includes('attachment')
                      );
                      const otherFields = fields.filter((f: any) => 
                        f !== responsibleField && f !== environmentField && f !== correctionDateField && 
                        f !== repairDescriptionField && f !== testChecklistField && f !== devicesField && 
                        f !== finalApprovalField && f !== partsUsedField && f !== commentsField && f !== attachmentsField
                      );
                      
                      return (
                        <div className="space-y-6">
                          {/* Other fields first - 3-column responsive grid */}
                          {otherFields.length > 0 && (
                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                              {otherFields.map((field: any) => renderField(field, true))}
                            </div>
                          )}
                          
                          {/* First level - Responsible Agent, Environment Conditions, and Correction Date */}
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {/* Responsible Agent - Left column */}
                            {responsibleField && (
                              <div className="w-full">
                                {renderField(responsibleField, true)}
                              </div>
                            )}
                            
                            {/* Environment Conditions - Center column */}
                            {environmentField && (
                              <div className="w-full">
                                {renderField(environmentField, true)}
                              </div>
                            )}
                            
                            {/* Correction Date - Right column */}
                            {correctionDateField && (
                              <div className="w-full">
                                {renderField(correctionDateField, true)}
                              </div>
                            )}
                          </div>
                          
                          {/* Second level - Repair Description, Test Checklist, and Devices Used side by side */}
                          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                            {/* Repair Description - Left column */}
                            {repairDescriptionField && (
                              <div className="w-full">
                                {renderField(repairDescriptionField, true)}
                              </div>
                            )}
                            
                            {/* Test Checklist with Final Approval - Center column */}
                            <div className="w-full space-y-4">
                              {testChecklistField && renderField(testChecklistField, true)}
                              {finalApprovalField && renderField(finalApprovalField, true)}
                            </div>
                            
                            {/* Devices Used - Right column */}
                            {devicesField && (
                              <div className="w-full">
                                {renderField(devicesField)}
                              </div>
                            )}
                          </div>
                          
                          {/* Parts Used table - Full width with optimal spacing */}
                          {partsUsedField && (
                            <div className="w-full">
                              {renderField(partsUsedField)}
                            </div>
                          )}
                          
                          {/* Attachments and Comments - Side by side below parts used */}
                          {(attachmentsField || commentsField) && (
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                              {/* Attachments - Left column */}
                              {attachmentsField && (
                                <div className="w-full">
                                  {renderField(attachmentsField, true)}
                                </div>
                              )}
                              
                              {/* Comments - Right column */}
                              {commentsField && (
                                <div className="w-full">
                                  {renderField(commentsField, true)}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    }
                    
                    // Enhanced layout for Step 5 - Quote & Approval with notification button
                    if (activeStep === 5) {
                      const quoteNumberField = fields.find((f: any) => f.name === 'quoteNumber');
                      const quoteDateField = fields.find((f: any) => f.name === 'quoteDate');
                      const quoteAgentField = fields.find((f: any) => f.name === 'quoteAgentId');
                      const approvalStatusField = fields.find((f: any) => f.name === 'approvalStatus');
                      const approvalDateField = fields.find((f: any) => f.name === 'approvalDate');
                      const invoiceNumberField = fields.find((f: any) => f.name === 'invoiceNumber');
                      const commentsField = fields.find((f: any) => f.name === 'comments');
                      const attachmentsField = fields.find((f: any) => f.type === 'file' || f.name === 'attachments');
                      const otherFields = fields.filter((f: any) => 
                        f !== quoteNumberField && f !== quoteDateField && f !== quoteAgentField && 
                        f !== approvalStatusField && f !== approvalDateField && f !== invoiceNumberField && 
                        f !== commentsField && f !== attachmentsField
                      );
                      
                      return (
                        <div className="space-y-6">
                          {/* Quote Notification Button - Prominent placement at top */}
                          <div className={`${isDarkMode ? 'bg-blue-900 border-blue-700' : 'bg-blue-50 border-blue-200'} border rounded-lg p-4`}>
                            <div className="flex items-center justify-between">
                              <div>
                                <h4 className={`text-sm font-medium ${isDarkMode ? 'text-blue-200' : 'text-blue-800'} mb-1`}>
                                  💰 Quote Notification
                                </h4>
                                <p className={`text-xs ${isDarkMode ? 'text-blue-300' : 'text-blue-600'}`}>
                                  Send notification to agent for quote creation and/or approval
                                </p>
                              </div>
                              <button
                                onClick={handleQuoteNotification}
                                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                                  isDarkMode 
                                    ? 'bg-blue-600 text-white hover:bg-blue-700' 
                                    : 'bg-blue-600 text-white hover:bg-blue-700'
                                }`}
                              >
                                💰 Request Quote
                              </button>
                            </div>
                          </div>

                          {/* Other fields first - 3-column responsive grid */}
                          {otherFields.length > 0 && (
                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                              {otherFields.map((field: any) => renderField(field, true))}
                            </div>
                          )}
                          
                          {/* First level - Quote Number, Quote Date, and Quote Agent */}
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {quoteNumberField && (
                              <div className="w-full">
                                {renderField(quoteNumberField, true)}
                              </div>
                            )}
                            
                            {quoteDateField && (
                              <div className="w-full">
                                {renderField(quoteDateField, true)}
                              </div>
                            )}
                            
                            {quoteAgentField && (
                              <div className="w-full">
                                {renderField(quoteAgentField, true)}
                              </div>
                            )}
                          </div>
                          
                          {/* Second level - Approval Status, Approval Date, and Invoice Number */}
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {approvalStatusField && (
                              <div className="w-full">
                                {renderField(approvalStatusField, true)}
                              </div>
                            )}
                            
                            {approvalDateField && (
                              <div className="w-full">
                                {renderField(approvalDateField, true)}
                              </div>
                            )}
                            
                            {invoiceNumberField && (
                              <div className="w-full">
                                {renderField(invoiceNumberField, true)}
                              </div>
                            )}
                          </div>
                          
                          {/* Comments and Attachments - Side by side */}
                          {(commentsField || attachmentsField) && (
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                              {commentsField && (
                                <div className="w-full">
                                  {renderField(commentsField, true)}
                                </div>
                              )}
                              
                              {attachmentsField && (
                                <div className="w-full">
                                  {renderField(attachmentsField, true)}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    }
                    
                    // Enhanced layout for other steps - Better responsiveness and field organization
                    const hasTables = fields.some((f: any) => f.type === 'parts_table' || f.type === 'devices_table');
                    const tableFields = fields.filter((f: any) => f.type === 'parts_table' || f.type === 'devices_table');
                    const textareaFields = fields.filter((f: any) => f.type === 'textarea');
                    const checklistFields = fields.filter((f: any) => f.type === 'checklist');
                    const regularFields = fields.filter((f: any) => 
                      f.type !== 'parts_table' && 
                      f.type !== 'devices_table' && 
                      f.type !== 'textarea' && 
                      f.type !== 'checklist'
                    );
                    
                    if (hasTables || textareaFields.length > 0 || checklistFields.length > 0) {
                      // Special layout for complex steps - Organized by field type
                      return (
                        <div className="space-y-6">
                          {/* Regular fields - 3-column responsive grid for better space utilization */}
                          {regularFields.length > 0 && (
                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                              {regularFields.map((field: any) => renderField(field, true))}
                            </div>
                          )}
                          
                          {/* Textarea and checklist fields - 2-column layout for better readability */}
                          {(textareaFields.length > 0 || checklistFields.length > 0) && (
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                              {textareaFields.map((field: any) => renderField(field, true))}
                              {checklistFields.map((field: any) => renderField(field, true))}
                            </div>
                          )}
                          
                          {/* Tables - Full width for proper display */}
                          {tableFields.length > 0 && (
                            <div className="space-y-4">
                              {tableFields.map((field: any) => renderField(field))}
                            </div>
                          )}
                        </div>
                      );
                    } else {
                      // Standard layout for simple steps - 3-column responsive grid
                      return (
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                          {/* Debug: Log if deviceModel field is present */}
                          {activeStep === 1 && (() => {
                            const deviceModelField = fields.find((f: any) => f.name === 'deviceModel');
                            console.log('🔍 Step 1 deviceModel field check:', deviceModelField ? 'FOUND' : 'NOT FOUND');
                            if (!deviceModelField) {
                              console.warn('⚠️ deviceModel field missing from step 1 fields:', fields.map((f: any) => f.name));
                            }
                            return null;
                          })()}
                          {fields.map((field: any) => renderField(field, true))}
                        </div>
                      );
                    }
                  })()
                ) : (
                  <div className="flex items-center justify-center h-32">
                    <div className="text-center">
                      <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'} text-sm mb-2`}>
                        {currentStepObj ? 'No fields defined for this step' : 'No step selected'}
                      </p>
                      {currentStepObj && (
                        <div className="text-xs text-gray-400 mt-2">
                          <p>Step: {currentStepObj.step_name}</p>
                          <p>Step Number: {currentStepObj.step_number}</p>
                          <p>Has Definition: {currentStepObj.definition ? 'Yes' : 'No'}</p>
                          <p>Fields Count: {currentStepObj.definition?.fields?.length || 0}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
          </div>
        </div>

        {/* PDF Reports Table - Show on all steps when workflow exists */}
        {workflow && (
          <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-white'} rounded border p-4 mt-4`}>
            <details className="group">
              <summary className={`${isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'} cursor-pointer text-sm font-medium mb-2 flex items-center justify-between`}>
                <span>Generated Reports ({pdfReports.length})</span>
                <div className="flex gap-2">
                  <button
                    onClick={loadPdfReports}
                    className="px-2 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600 transition-colors"
                  >
                    Reload
                  </button>
                  <button
                    onClick={generatePdfReport}
                    disabled={generatingPdf || (workflow?.currentStep || 1) < 6}
                    className={`px-2 py-1 text-white text-xs rounded transition-colors disabled:opacity-50 ${
                      (workflow?.currentStep || 1) >= 6 
                        ? 'bg-purple-500 hover:bg-purple-600' 
                        : 'bg-gray-400 cursor-not-allowed'
                    }`}
                    title={(workflow?.currentStep || 1) < 6 ? 'Report generation available from step 6 onwards' : 'Generate new service report'}
                  >
                    {generatingPdf ? 'Generating...' : 'Generate'}
                  </button>
                </div>
              </summary>
              
              {pdfReports.length > 0 ? (
                <div className="overflow-x-auto mt-3">
                  <table className="min-w-full table-auto text-xs">
                    <thead>
                      <tr className={`${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
                        <th className={`px-2 py-1 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase`}>Status</th>
                        <th className={`px-2 py-1 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase`}>Date & Time</th>
                        <th className={`px-2 py-1 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase`}>File</th>
                        <th className={`px-2 py-1 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase`}>Size</th>
                        <th className={`px-2 py-1 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase`}>Repair Signer</th>
                        <th className={`px-2 py-1 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase`}>Approval Signer</th>
                        <th className={`px-2 py-1 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase`}>Actions</th>
                      </tr>
                    </thead>
                    <tbody className={`${isDarkMode ? 'divide-gray-600' : 'divide-gray-200'} divide-y`}>
                      {pdfReports.map((report, index) => (
                        <tr key={index} className={`${isDarkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-50'}`}>
                          <td className="px-2 py-1 text-xs">
                            {/* PDF-SPECIFIC STATUS BADGE */}
                            <span 
                              className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium text-white"
                              style={{ backgroundColor: report.statusColor || '#fbbf24' }}
                            >
                              📊 {report.statusDescription || 'Draft - No signatures'}
                            </span>
                          </td>
                          <td className="px-2 py-1 text-xs">
                            <div className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                              <div>{new Date(report.createdAt).toLocaleDateString()}</div>
                              <div className="text-xs opacity-75">{new Date(report.createdAt).toLocaleTimeString()}</div>
                            </div>
                          </td>
                          <td className="px-2 py-1 text-xs">
                            <div className={`font-mono ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} break-all`}>
                              {report.filename}
                            </div>
                          </td>
                          <td className="px-2 py-1 text-xs">
                            <div className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {report.formattedSize || `${Math.round(report.size / 1024)} KB`}
                            </div>
                          </td>
                          <td className="px-2 py-1 text-xs">
                            <div className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                              {(() => {
                                // DEBUG: Log the report data to see what's available
                                console.log('🔍 Report data for signers:', {
                                  filename: report.filename,
                                  repairSigner: report.repairSigner,
                                  approvalSigner: report.approvalSigner,
                                  allReportKeys: Object.keys(report)
                                });
                                
                                if (report.repairSigner) {
                                  return (
                                    <div>
                                      <div className="font-medium">{report.repairSigner.name}</div>
                                      <div className="text-xs opacity-75">{report.repairSigner.email}</div>
                                    </div>
                                  );
                                } else {
                                  return <span className="text-gray-400 italic">Not signed</span>;
                                }
                              })()}
                            </div>
                          </td>
                          <td className="px-2 py-1 text-xs">
                            <div className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                              {(() => {
                                if (report.approvalSigner) {
                                  return (
                                    <div>
                                      <div className="font-medium">{report.approvalSigner.name}</div>
                                      <div className="text-xs opacity-75">{report.approvalSigner.email}</div>
                                    </div>
                                  );
                                } else {
                                  return <span className="text-gray-400 italic">Not signed</span>;
                                }
                              })()}
                            </div>
                          </td>
                          <td className="px-2 py-1 text-xs">
                            <div className="flex flex-wrap gap-1">
                              <button
                                onClick={() => downloadPdfReport(report.filename)}
                                className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'text-blue-400 hover:text-blue-300 hover:bg-gray-700' : 'text-blue-600 hover:text-blue-800 hover:bg-blue-50'}`}
                              >
                                Open
                              </button>
                              
                              {/* Repair Signature Button - Always enabled */}
                              {report.reportType === 'draft' && (
                                <button
                                  onClick={() => handleRepairSignature()}
                                  disabled={false}
                                  className="text-xs px-2 py-1 rounded bg-green-600 text-white hover:bg-green-700"
                                >
                                  🖋️ Sign Repair
                                </button>
                              )}
                              
                              {/* Request Approval Button - NEW */}
                              {report.reportType === 'draft' && (
                                <button
                                  onClick={() => handleRequestApproval()}
                                  disabled={false}
                                  className="text-xs px-2 py-1 rounded bg-orange-600 text-white hover:bg-orange-700 transition-colors"
                                >
                                  🔔 Request Approval
                                </button>
                              )}
                              
                              {/* Approval Signature Button - Always enabled */}
                              {report.reportType === 'draft' && (
                                <button
                                  onClick={() => handleApprovalSignature()}
                                  disabled={false}
                                  className="text-xs px-2 py-1 rounded transition-colors bg-purple-600 text-white hover:bg-purple-700"
                                >
                                  ✅ Approve & Sign
                                </button>
                              )}
                              

                              
                              <button
                                onClick={() => deletePdfReport(report.filename)}
                                className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'text-red-400 hover:text-red-300 hover:bg-gray-700' : 'text-red-600 hover:text-red-800 hover:bg-red-50'}`}
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className={`text-center py-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} text-sm`}>
                  <p>No reports generated yet</p>
                </div>
              )}
            </details>
          </div>
        )}

        {/* Step Navigation */}
        <div className="flex justify-between items-center mt-4">
          <button
            onClick={() => {
              if (activeStep > 1) {
                handleStepClick(activeStep - 1);
              }
            }}
            disabled={activeStep <= 1 || isEditing}
            className={`px-3 py-1.5 text-sm ${isDarkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-800'} transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            ← Previous
          </button>

          <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Step {activeStep} / {workflow?.steps?.length || 0}
          </div>

          <button
            onClick={() => {
              if (activeStep < (workflow?.steps?.length || 0)) {
                handleStepClick(activeStep + 1);
              }
            }}
            disabled={activeStep >= (workflow?.steps?.length || 0) || isEditing}
            className={`px-3 py-1.5 text-sm ${isDarkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-800'} transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            Next →
          </button>
        </div>

        {/* Simplified Signature Modal */}
        {showSignatureModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className={`rounded-lg p-6 max-w-2xl w-full mx-4 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
              <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {signatureType === 'repair' ? 'Sign Repair Report' : 'Sign Approval'}
              </h3>
              
              <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Draw your signature below and click Save to sign the report.<br/>
                <span className="font-medium text-blue-600">Your current agent profile ({user?.name} - {user?.email}) will be used automatically.</span>
              </p>

              <DigitalSignature
                onSignatureCapture={(signatureDataUrl) => {
                  if (signatureType === 'repair') {
                    completeRepairSignature(signatureDataUrl);
                  } else {
                    completeApprovalSignature(signatureDataUrl);
                  }
                }}
                onCancel={() => setShowSignatureModal(false)}
                width={500}
                height={150}
                showControls={true}
              />
            </div>
          </div>
        )}

        {/* Request Approval Modal */}
        {showRequestApprovalModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className={`rounded-lg p-6 max-w-md w-full mx-4 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
              <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Request Approval
              </h3>
              
              <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Select an agent to notify about this approval request. They will receive both a system notification and an email with a direct link to review the report.
              </p>

              <div className="mb-4">
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Select Agent
                </label>
                {availableApprovers.length > 0 ? (
                  <select
                    value={selectedNotificationAgent}
                    onChange={(e) => setSelectedNotificationAgent(e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md ${
                      isDarkMode 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  >
                    <option value="">Choose an agent...</option>
                    {availableApprovers.map((agent) => {
                      console.log('🎯 Rendering agent option:', agent);
                      return (
                        <option key={agent.id} value={agent.id}>
                          {agent.firstName} {agent.lastName} ({agent.email})
                        </option>
                      );
                    })}
                  </select>
                ) : (
                  <div className={`p-3 rounded-md border ${
                    isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-gray-300' 
                      : 'bg-gray-100 border-gray-300 text-gray-600'
                  }`}>
                    No agents available. Please try again later.
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowRequestApprovalModal(false);
                    setSelectedNotificationAgent('');
                  }}
                  className={`px-4 py-2 rounded-md ${
                    isDarkMode
                      ? 'bg-gray-600 text-white hover:bg-gray-700'
                      : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={sendApprovalNotification}
                  disabled={!selectedNotificationAgent || sendingNotification || availableApprovers.length === 0}
                  className={`px-4 py-2 rounded-md text-white ${
                    !selectedNotificationAgent || sendingNotification || availableApprovers.length === 0
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-orange-600 hover:bg-orange-700'
                  }`}
                >
                  {sendingNotification ? 'Sending...' : 'Send Notification'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Quote Notification Modal */}
        {showQuoteNotificationModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className={`rounded-lg p-6 max-w-md w-full mx-4 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
              <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                💰 Request Quote
              </h3>
              
              <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Select an agent to notify about this quote request. They will receive both a system notification and an email with a direct link to create and/or approve the customer quote for this service.
              </p>

              <div className="mb-4">
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Select Agent
                </label>
                {availableApprovers.length > 0 ? (
                  <select
                    value={selectedQuoteAgent}
                    onChange={(e) => setSelectedQuoteAgent(e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md ${
                      isDarkMode 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  >
                    <option value="">Choose an agent...</option>
                    {availableApprovers.map((agent) => {
                      return (
                        <option key={agent.id} value={agent.id}>
                          {agent.firstName} {agent.lastName} ({agent.email})
                        </option>
                      );
                    })}
                  </select>
                ) : (
                  <div className={`p-3 rounded-md border ${
                    isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-gray-300' 
                      : 'bg-gray-100 border-gray-300 text-gray-600'
                  }`}>
                    No agents available. Please try again later.
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowQuoteNotificationModal(false);
                    setSelectedQuoteAgent('');
                  }}
                  className={`px-4 py-2 rounded-md ${
                    isDarkMode
                      ? 'bg-gray-600 text-white hover:bg-gray-700'
                      : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={sendQuoteNotification}
                  disabled={!selectedQuoteAgent || sendingQuoteNotification || availableApprovers.length === 0}
                  className={`px-4 py-2 rounded-md text-white ${
                    !selectedQuoteAgent || sendingQuoteNotification || availableApprovers.length === 0
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700'
                  }`}
                >
                  {sendingQuoteNotification ? 'Sending...' : 'Send Quote Request'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Approval Selection Modal - Removed for simplified flow */}

      </div>
    </div>
  );
};

export default ServiceWorkflow; 